import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { TimeEntryListSkeleton, Skeleton } from '../../components/common/Skeleton';
import { useScrollLoadTrigger } from '../../hooks/useScrollLoadTrigger';
import * as XLSX from 'xlsx';
import {
  Clock, Plus, Search, Filter, RefreshCw, Calendar, CheckCircle, Edit2, Trash2, X, Save, Download,
  TrendingUp, AlertCircle, Check, User, Play, Pause, Square, ScanBarcode, Timer, Loader2,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
} from 'lucide-react';

import {
  getTimeEntries, getTimeEntriesByEmployee, getRunningEntries,
  createTimeEntry as apiCreateTimeEntry, updateTimeEntry as apiUpdateTimeEntry,
  deleteTimeEntry as apiDeleteTimeEntry, startTimeEntry as apiStartTimeEntry,
  stopTimeEntry as apiStopTimeEntry, stopAndStartTimeEntry as apiStopAndStart,
  getProductionTaskByBarcode, getTimeEntryHistoryByTask,
} from '../../api/timeEntry.api';
import {
  getProductionTaskCount, getProductionLotSummaries, getAllFullProductionLots,
  startProductionTask, completeProductionTask, syncProductionTasksFromSap, getProductionTaskById
} from '../../api/productionTask.api';
import { getMyTasks, getStatusName, getPriorityName, getStatusBadgeColor, getPriorityBadgeColor } from '../../api/taskAssignmentApi';
import TaskPickerModal from '../../components/tasks/TaskPickerModal';
import AnimatedClock from '../../components/common/AnimatedClock';
import ConstellationTimeline from '../../components/timeline/ConstellationTimeline';
import { Layers, Eye, StickyNote, Sparkles, List, Database } from 'lucide-react';
import toast from 'react-hot-toast';
import { UserRole } from '../../constants/userRole';

// ----------------------------------------------------------------------
// Local time formatting utilities
// ----------------------------------------------------------------------
const parseUTC = (val) => {
  if (!val) return new Date();
  const s = typeof val === 'string' ? val : String(val);
  if (s.endsWith('Z') || s.includes('+') || /[+-]\d{2}:\d{2}$/.test(s)) return new Date(s);
  return new Date(s + 'Z');
};

const formatLocalStartTime = (entry) => {
  if (entry?.startedAt) {
    return new Date(entry.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (entry?.startTime && entry?.date) {
    const dt = new Date(`${entry.date.slice(0, 10)}T${entry.startTime}:00Z`);
    return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (entry?.startTime) return entry.startTime;
  return '-';
};

const formatLocalEndTime = (entry) => {
  if (entry?.endedAt) {
    return new Date(entry.endedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (entry?.endTime && entry?.date) {
    const dt = new Date(`${entry.date.slice(0, 10)}T${entry.endTime}:00Z`);
    return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (entry?.endTime) return entry.endTime;
  return '-';
};

const formatLocalDateTime = (val) => {
  if (!val) return '-';
  return new Date(val).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ----------------------------------------------------------------------
// Helper to store/retrieve local start time for running timers
// ----------------------------------------------------------------------
const getLocalStartTimeKey = (entryId) => `operis_local_start_${entryId}`;

const setLocalStartTime = (entryId, startTimeMs) => {
  sessionStorage.setItem(getLocalStartTimeKey(entryId), startTimeMs.toString());
};

const getLocalStartTime = (entryId) => {
  const val = sessionStorage.getItem(getLocalStartTimeKey(entryId));
  return val ? parseInt(val, 10) : null;
};

const removeLocalStartTime = (entryId) => {
  sessionStorage.removeItem(getLocalStartTimeKey(entryId));
};

// ----------------------------------------------------------------------
const TimeEntryPage = () => {
  const { user, setSyncInBackground: globalSetSyncInBackground } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const employeeUUId = user?.sapEmployeeUUID || user?.id || '';
  const employeeName = user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || t('commonUi.user');
  const pauseStorageKey = `operis_timer_pause_${employeeUUId}`;

  // ── State ──
  const [timeEntries, setTimeEntries] = useState([]);
  const [productionTasks, setProductionTasks] = useState([]);
  const [myAssignedTasks, setMyAssignedTasks] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(null);
  const [stopNotes, setStopNotes] = useState('');
  const [stopActivity, setStopActivity] = useState(null);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [runningEntries, setRunningEntries] = useState([]);
  const [elapsedMap, setElapsedMap] = useState({});
  const [pausedMap, setPausedMap] = useState({});
  const tickRef = useRef(null);

  // --------------------------------------------------------------------
  // Timer tick – uses local start time if available, else falls back to backend
  // --------------------------------------------------------------------
  tickRef.current = () => {
    if (runningEntries.length === 0) {
      setElapsedMap(prev => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }
    const map = {};
    const now = Date.now();
    runningEntries.forEach(entry => {
      if (pausedMap[entry.id] !== undefined) {
        const diff = pausedMap[entry.id];
        const h = String(Math.floor(diff / 3600)).padStart(2, '0');
        const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
        const s = String(diff % 60).padStart(2, '0');
        map[entry.id] = `${h}:${m}:${s}`;
        return;
      }
      let startMs = getLocalStartTime(entry.id);
      let pauseOffset = entry._pauseOffset || 0;
      let accumulated = entry.accumulatedSeconds || 0;

      if (startMs === null) {
        const backendDate = parseUTC(entry.startedAt || entry.createdAt);
        startMs = backendDate.getTime();
      }
      const diff = Math.max(0, Math.floor((now - startMs - pauseOffset) / 1000) + accumulated);
      const h = String(Math.floor(diff / 3600)).padStart(2, '0');
      const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
      const s = String(diff % 60).padStart(2, '0');
      map[entry.id] = `${h}:${m}:${s}`;
    });
    setElapsedMap(map);
  };

  useEffect(() => {
    tickRef.current?.();
    const id = setInterval(() => tickRef.current?.(), 1000);
    return () => clearInterval(id);
  }, []);

  // --------------------------------------------------------------------
  // Rest of state and handlers
  // --------------------------------------------------------------------
  const [barcode, setBarcode] = useState('');
  const [barcodeTask, setBarcodeTask] = useState(null);
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [barcodeError, setBarcodeError] = useState('');
  const barcodeRef = useRef(null);
  const [timerDescription, setTimerDescription] = useState('');
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [taskHistory, setTaskHistory] = useState([]);
  const [showPreviousSessions, setShowPreviousSessions] = useState(false);
  const [continueFromEntry, setContinueFromEntry] = useState(null);
  const [notesViewEntry, setNotesViewEntry] = useState(null);
  const [expandedTaskGroups, setExpandedTaskGroups] = useState({});
  const [activityConflict, setActivityConflict] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('timeEntryViewMode') || 'timeline');
  useEffect(() => { localStorage.setItem('timeEntryViewMode', viewMode); }, [viewMode]);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const [showFilters, setShowFilters] = useState(false);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const STATUS_OPTIONS = [
    { value: '', label: t('commonUi.all') },
    { value: 'In Process', label: t('enums.in_progress') },
    { value: 'Finished', label: t('enums.completed') },
    { value: 'Not Started', label: t('commonUi.none') },
  ];

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    description: '',
    referenceProductionTaskId: '',
    timeTypeCode: 'LABOR',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showTaskPicker, setShowTaskPicker] = useState(false);
  const [taskPickerInitialTaskId, setTaskPickerInitialTaskId] = useState(null);
  const [taskPickerInitialOpenKey, setTaskPickerInitialOpenKey] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const [fullTasksLoaded, setFullTasksLoaded] = useState(false);
  const [taskPickerLoading, setTaskPickerLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncInBackground, setSyncInBackground] = useState(false);
  const [taskPickerPage, setTaskPickerPage] = useState(1);
  const [taskPickerTotalPages, setTaskPickerTotalPages] = useState(1);
  const [taskPickerTotalCount, setTaskPickerTotalCount] = useState(0);
  const [taskPickerPageLoading, setTaskPickerPageLoading] = useState(false);
  const [taskPickerSearchTerm, setTaskPickerSearchTerm] = useState('');
  const [taskPickerDebouncedSearch, setTaskPickerDebouncedSearch] = useState('');
  const [taskPickerFilterStatus, setTaskPickerFilterStatus] = useState('');
  const TASK_PICKER_PAGE_SIZE = 20;

  // Debounce for task picker search
  useEffect(() => {
    const handler = setTimeout(() => {
      setTaskPickerDebouncedSearch(taskPickerSearchTerm);
    }, 400);
    return () => clearTimeout(handler);
  }, [taskPickerSearchTerm]);

  const [entriesPage, setEntriesPage] = useState(1);
  const [entriesTotalPages, setEntriesTotalPages] = useState(1);
  const [entriesTotalCount, setEntriesTotalCount] = useState(0);
  const [entriesPageLoading, setEntriesPageLoading] = useState(false);
  const [entriesPageSize, setEntriesPageSize] = useState(10);
  const CONSTELLATION_PAGE_SIZE = 120;
  const PAGE_SIZE_OPTIONS = [5, 10, 15, 20];
  const timeEntriesRef = useRef([]);

  const [stopModalActivities, setStopModalActivities] = useState([]);
  const [stopModalLoading, setStopModalLoading] = useState(false);

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);
  const nowTimeStr = () => new Date().toTimeString().slice(0, 5);
  const todayStr = () => new Date().toISOString().split('T')[0];

  useEffect(() => {
    timeEntriesRef.current = timeEntries;
  }, [timeEntries]);

  const mergeEntriesById = useCallback((currentEntries, nextEntries) => {
    const merged = [...currentEntries, ...nextEntries];
    const deduped = Array.from(new Map(merged.map((entry) => [entry.id, entry])).values());
    return deduped.sort((a, b) => {
      const dateA = (a.date || '').slice(0, 10);
      const dateB = (b.date || '').slice(0, 10);
      if (dateA !== dateB) return dateB.localeCompare(dateA);
      return (b.startTime || '').localeCompare(a.startTime || '');
    });
  }, []);

  // --------------------------------------------------------------------
  // Pause / Resume
  // --------------------------------------------------------------------
  const handlePauseTimer = useCallback((entryId) => {
    const entry = runningEntries.find(e => e.id === entryId);
    if (!entry) return;
    let startMs = getLocalStartTime(entryId);
    if (startMs === null) {
      const backendDate = parseUTC(entry.startedAt || entry.createdAt);
      startMs = backendDate.getTime();
    }
    const pauseOffset = entry._pauseOffset || 0;
    const frozenSecs = Math.max(0, Math.floor((Date.now() - startMs - pauseOffset) / 1000));
    const pausedAt = Date.now();
    setPausedMap(prev => ({ ...prev, [entryId]: frozenSecs }));
    setRunningEntries(prev => prev.map(e => e.id === entryId ? { ...e, _pausedAt: pausedAt } : e));
    const savedPause = JSON.parse(sessionStorage.getItem(pauseStorageKey) || '{}');
    savedPause[entryId] = { frozenSecs, pausedAt, pauseOffset };
    sessionStorage.setItem(pauseStorageKey, JSON.stringify(savedPause));
    showNotification(t('commonUi.pause'));
  }, [runningEntries, pauseStorageKey, showNotification, t]);

  const handleResumeTimer = useCallback((entryId) => {
    setRunningEntries(prev => prev.map(e => {
      if (e.id !== entryId) return e;
      const pausedDuration = e._pausedAt ? Date.now() - e._pausedAt : 0;
      return { ...e, _pauseOffset: (e._pauseOffset || 0) + pausedDuration, _pausedAt: undefined };
    }));
    setPausedMap(prev => { const next = { ...prev }; delete next[entryId]; return next; });
    const savedPause = JSON.parse(sessionStorage.getItem(pauseStorageKey) || '{}');
    delete savedPause[entryId];
    sessionStorage.setItem(pauseStorageKey, JSON.stringify(savedPause));
    showNotification(t('commonUi.resume'));
  }, [pauseStorageKey, showNotification, t]);

  // ── Fetch initial data (tasks) ──
  useEffect(() => {
    const load = async () => {
      try {
        const [countRes, assignedRes] = await Promise.all([getProductionTaskCount(), getMyTasks()]);
        if (countRes.isSuccess) setTaskCount(countRes.data || 0);
        if (assignedRes.isSuccess && assignedRes.data) setMyAssignedTasks(assignedRes.data || []);
      } catch (err) { console.error(t('pages.timeEntry.toastSyncFailed'), err); }
    };
    load();
  }, [t]);

  const loadFullTasks = useCallback(async (page = 1, search = '', status = '') => {
    // Use cache when opening modal without active filters
    if (!search && !status && page === taskPickerPage && fullTasksLoaded && productionTasks.length > 0) {
      setShowTaskPicker(true);
      return;
    }
    setTaskPickerLoading(page === 1 && !fullTasksLoaded);
    setTaskPickerPageLoading(true);
    try {
      const params = { pageNumber: page, pageSize: TASK_PICKER_PAGE_SIZE };
      if (search.trim()) params.searchTerm = search.trim();
      if (status) params.Status = status;
      const res = await getProductionLotSummaries(params);
      let items = [], totalCount = 0, totalPages = 1;
      if (res?.items && Array.isArray(res.items)) {
        items = res.items;
        totalCount = res.totalCount || items.length;
        totalPages = res.totalPages || Math.ceil(totalCount / TASK_PICKER_PAGE_SIZE);
      } else if (res?.data && Array.isArray(res.data)) {
        items = res.data;
        totalCount = res.totalCount || items.length;
        totalPages = res.totalPages || Math.ceil(totalCount / TASK_PICKER_PAGE_SIZE);
      } else if (Array.isArray(res)) {
        items = res;
        totalCount = res.length;
        totalPages = 1;
      }
      setProductionTasks(items);
      setTaskCount(totalCount);
      setTaskPickerPage(page);
      setTaskPickerTotalPages(totalPages);
      setTaskPickerTotalCount(totalCount);
      setFullTasksLoaded(true);
    } catch (err) { console.error(t('pages.timeEntry.toastSyncFailed'), err); }
    setTaskPickerLoading(false);
    setTaskPickerPageLoading(false);
    setShowTaskPicker(true);
  }, [fullTasksLoaded, t]);

  // Reload task picker when debounced search or status changes
  useEffect(() => {
    if (!taskPickerDebouncedSearch && !taskPickerFilterStatus && fullTasksLoaded) return;
    if (!taskPickerDebouncedSearch && !taskPickerFilterStatus && productionTasks.length === 0) return;
    setFullTasksLoaded(false);
    setTaskPickerPage(1);
    loadFullTasks(1, taskPickerDebouncedSearch, taskPickerFilterStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskPickerDebouncedSearch, taskPickerFilterStatus]);

  const handleTaskPickerPageChange = useCallback((page) => {
    loadFullTasks(page, taskPickerDebouncedSearch, taskPickerFilterStatus);
  }, [loadFullTasks, taskPickerDebouncedSearch, taskPickerFilterStatus]);

  useEffect(() => {
    const shouldOpenTask = searchParams.get('openTask') === 'true';
    if (!shouldOpenTask) return;

    const targetTaskId =
      searchParams.get('productionLotId') ||
      searchParams.get('taskId') ||
      searchParams.get('productionTaskId') ||
      searchParams.get('search');

    if (!targetTaskId) {
      showNotification('No production task was linked to this notification', 'error');
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('openTask');
      setSearchParams(nextParams, { replace: true });
      return;
    }

    setTaskPickerInitialTaskId(targetTaskId);
    setTaskPickerInitialOpenKey(prev => prev + 1);
    setShowTaskPicker(true);

    if (!fullTasksLoaded && productionTasks.length === 0) {
      loadFullTasks(1);
    }

    const nextParams = new URLSearchParams(searchParams);
    ['openTask', 'productionLotId', 'taskId', 'productionTaskId', 'productionOrderId', 'search'].forEach(key => {
      nextParams.delete(key);
    });
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, showNotification, fullTasksLoaded, productionTasks.length, loadFullTasks]);

  const handleSyncFromSap = useCallback(async () => {
    setSyncLoading(true);
    setSyncInBackground(false);
    globalSetSyncInBackground(false);
    try {
      const result = await syncProductionTasksFromSap();
      const data = result.data;
      if (data?.imported > 0) {
        const skippedLine = data.skipped > 0 ? t('pages.timeEntry.skippedAlready', { skipped: data.skipped }) : '';
        toast.success(
          <div><strong className="block mb-1">{t('pages.timeEntry.sapSyncCompleteTitle')}</strong><span className="text-sm opacity-90">{t('pages.timeEntry.sapSyncImportedLine', { imported: data.imported, skippedLine })}</span></div>,
          { duration: 4000, icon: '🔄' }
        );
        setFullTasksLoaded(false);
        try { const countRes = await getProductionTaskCount(); if (countRes.isSuccess) setTaskCount(countRes.data || 0); } catch (_) { }
      } else if (data?.totalFound === 0) {
        toast(<div><strong className="block mb-1">{t('pages.timeEntry.noTasksTitle')}</strong><span className="text-sm opacity-90">{t('pages.timeEntry.noTasksBody')}</span></div>, { duration: 3000, icon: 'ℹ️' });
      } else {
        toast(<div><strong className="block mb-1">{t('pages.timeEntry.upToDateTitle')}</strong><span className="text-sm opacity-90">{t('pages.timeEntry.upToDateBody', { count: data?.skipped || 0 })}</span></div>, { duration: 3000, icon: '✅' });
      }
    } catch (error) {
      console.error('SAP sync failed:', error);
      toast.error(<div><strong className="block mb-1">{t('pages.timeEntry.toastSyncFailed')}</strong><span className="text-sm">{error.message || t('pages.timeEntry.toastSyncFailedMsg')}</span></div>, { duration: 4000 });
    } finally {
      setSyncLoading(false);
      setSyncInBackground(false);
      globalSetSyncInBackground(false);
    }
  }, [t, globalSetSyncInBackground]);

  const handleContinueInBackground = useCallback(() => {
    setSyncLoading(false);
    setSyncInBackground(true);
    globalSetSyncInBackground(true);
    toast(t('pages.timeEntry.syncingBackground'), { icon: '🔄', duration: 3000 });
  }, [t, globalSetSyncInBackground]);

  const formatBackendDate = (dateStr) => {
    if (!dateStr) return new Date();
    try {
      return parseUTC(dateStr);
    } catch {
      return new Date(dateStr);
    }
  };

  const fetchData = useCallback(async (page = 1, pageSize = entriesPageSize, options = {}) => {
    const { append = false, isConstellation = viewMode === 'timeline' } = options;
    const effectivePageSize = isConstellation ? Math.max(pageSize, CONSTELLATION_PAGE_SIZE) : pageSize;
    try {
      if (page === 1 && !append) setLoading(true);
      else setEntriesPageLoading(true);
      const entriesRes = await getTimeEntriesByEmployee(employeeUUId, page, pageSize, viewMode === 'timeline');
      let entries = [], totalCount = 0, totalPages = 1;
      if (entriesRes.isSuccess && entriesRes.data) {
        if (Array.isArray(entriesRes.data)) { entries = entriesRes.data; totalCount = entries.length; totalPages = 1; }
        else { entries = entriesRes.data.items || []; totalCount = entriesRes.data.totalCount || entries.length; totalPages = entriesRes.data.totalPages || Math.ceil(totalCount / effectivePageSize); }
      }
      if (debouncedSearch) {
        const s = debouncedSearch.toLowerCase();
        entries = entries.filter(e => (e.description || '').toLowerCase().includes(s) || (e.referenceProductionTaskId || '').toLowerCase().includes(s) || (e.employeeName || '').toLowerCase().includes(s));
      }
      if (filterDateFrom) entries = entries.filter(e => (e.date || '').slice(0, 10) >= filterDateFrom);
      if (filterDateTo) entries = entries.filter(e => (e.date || '').slice(0, 10) <= filterDateTo);
      if (filterStatus) entries = entries.filter(e => e.processingStatusCode === filterStatus);
      const nextEntries = append ? mergeEntriesById(timeEntriesRef.current, entries) : entries;
      setTimeEntries(nextEntries);
      setEntriesPage(page);
      setEntriesTotalPages(totalPages);
      setEntriesTotalCount(totalCount);
      const weekStart = new Date(); weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      const weekStartStr = weekStart.toISOString().split('T')[0];
      const weekEntries = nextEntries.filter(e => (e.date || '').slice(0, 10) >= weekStartStr);
      const totalHours = weekEntries.reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
      setWeeklySummary({ totalHours: Math.round(totalHours * 10) / 10, entriesCount: weekEntries.length });
      if (employeeUUId) {
        const runRes = await getRunningEntries(employeeUUId);
        if (runRes.isSuccess && runRes.data) {
          const fetchedEntries = Array.isArray(runRes.data) ? runRes.data : [runRes.data];
          const savedPause = JSON.parse(sessionStorage.getItem(pauseStorageKey) || '{}');
          const restoredPauseMap = {};
          setRunningEntries(prev => {
            const activitiesLookup = {};
            prev.forEach(pe => { if (pe._activities?.length > 0) { if (pe.id) activitiesLookup[pe.id] = pe._activities; if (pe.referenceProductionTaskId) activitiesLookup[pe.referenceProductionTaskId] = pe._activities; } });
            const entriesWithMeta = fetchedEntries.map(e => {
              const saved = savedPause[e.id];
              const activities = activitiesLookup[e.id] || activitiesLookup[e.referenceProductionTaskId] || [];
              const base = { ...e, _activities: activities };
              if (saved) { restoredPauseMap[e.id] = saved.frozenSecs; return { ...base, _pausedAt: saved.pausedAt, _pauseOffset: saved.pauseOffset }; }
              return base;
            });
            return entriesWithMeta;
          });
          setPausedMap(restoredPauseMap);
        } else { setRunningEntries([]); setPausedMap({}); }
      }
    } catch (err) { console.error(t('pages.timeEntry.toastSyncFailed'), err); showNotification(t('commonUi.error'), 'error'); }
    finally { setLoading(false); setEntriesPageLoading(false); }
  }, [employeeUUId, debouncedSearch, filterDateFrom, filterDateTo, filterStatus, showNotification, entriesPageSize, pauseStorageKey, viewMode, mergeEntriesById, t]);

  const handleEntriesPageChange = useCallback((page) => {
    fetchData(page, entriesPageSize, { append: false, isConstellation: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [fetchData, entriesPageSize]);
  const handlePageSizeChange = useCallback((newSize) => {
    setEntriesPageSize(newSize);
    fetchData(1, newSize, { append: false, isConstellation: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [fetchData]);
  const handleLoadMoreTimeline = useCallback(() => {
    if (viewMode !== 'timeline' || entriesPageLoading || loading || entriesPage >= entriesTotalPages) return;
    fetchData(entriesPage + 1, CONSTELLATION_PAGE_SIZE, { append: true, isConstellation: true });
  }, [viewMode, entriesPageLoading, loading, entriesPage, entriesTotalPages, fetchData, CONSTELLATION_PAGE_SIZE]);

  const listScrollSentinelRef = useScrollLoadTrigger({
    onLoadMore: handleLoadMoreTimeline,
    hasMore: viewMode === 'timeline' && entriesPage < entriesTotalPages && entriesTotalCount > 0,
    isLoading: entriesPageLoading || loading,
    disabled: viewMode !== 'timeline',
    rootMargin: '0px 0px 160px 0px',
  });
  useEffect(() => {
    fetchData(1, viewMode === 'timeline' ? CONSTELLATION_PAGE_SIZE : entriesPageSize, {
      append: false,
      isConstellation: viewMode === 'timeline',
    });
  }, [fetchData, viewMode, entriesPageSize, CONSTELLATION_PAGE_SIZE]);

  // --------------------------------------------------------------------
  // EXPORT FUNCTION - Exports ALL data with bold headers
  // --------------------------------------------------------------------
  const handleExport = useCallback(async () => {
    const exportToastId = toast.loading(t('pages.timeEntry.exportLoading'));

    try {
      const currentSearchTerm = debouncedSearch;
      const currentFilterDateFrom = filterDateFrom;
      const currentFilterDateTo = filterDateTo;
      const currentFilterStatus = filterStatus;

      let allEntries = [];
      let currentPageNum = 1;
      let hasMore = true;
      let totalPagesFromApi = 1;

      while (hasMore) {
        const res = await getTimeEntriesByEmployee(employeeUUId, currentPageNum, 100);
        let pageEntries = [];
        let totalCount = 0;

        if (res.isSuccess && res.data) {
          if (Array.isArray(res.data)) {
            pageEntries = res.data;
            totalCount = pageEntries.length;
            totalPagesFromApi = 1;
            hasMore = false;
          } else {
            pageEntries = res.data.items || [];
            totalCount = res.data.totalCount || pageEntries.length;
            totalPagesFromApi = res.data.totalPages || Math.ceil(totalCount / 100);
            hasMore = currentPageNum < totalPagesFromApi;
          }
        } else {
          hasMore = false;
        }

        if (currentSearchTerm) {
          const s = currentSearchTerm.toLowerCase();
          pageEntries = pageEntries.filter(e =>
            (e.description || '').toLowerCase().includes(s) ||
            (e.referenceProductionTaskId || '').toLowerCase().includes(s) ||
            (e.employeeName || '').toLowerCase().includes(s)
          );
        }
        if (currentFilterDateFrom) {
          pageEntries = pageEntries.filter(e => (e.date || '').slice(0, 10) >= currentFilterDateFrom);
        }
        if (currentFilterDateTo) {
          pageEntries = pageEntries.filter(e => (e.date || '').slice(0, 10) <= currentFilterDateTo);
        }
        if (currentFilterStatus) {
          pageEntries = pageEntries.filter(e => e.processingStatusCode === currentFilterStatus);
        }

        allEntries.push(...pageEntries);
        currentPageNum++;

        if (currentPageNum > 100) break;
      }

      if (allEntries.length === 0) {
        toast.error(t('pages.timeEntry.toastNoExport'), { duration: 3000, id: exportToastId });
        return;
      }

      const exportData = allEntries.map(entry => {
        const dateStr = (entry.date || '').slice(0, 10);
        const startTime = entry.startTime || '';
        const endTime = entry.endTime || '';
        const hours = entry.hours || 0;
        const description = entry.description || '';
        const referenceProductionTaskId = entry.referenceProductionTaskId || '';
        const productionTaskId = entry.productionTaskId || '';
        const activityName = entry.activityName || entry.activityId || '';
        const status = entry.isRunning ? t('enums.in_progress') : (entry.processingStatusCode || '');
        const employee = entry.employeeName || employeeName || '';
        const notes = entry.notes || '';
        const version = entry.version || 1;

        return {
          [t('commonUi.date')]: dateStr,
          [t('pages.timeEntry.startTime')]: startTime,
          [t('pages.timeEntry.endTime')]: endTime,
          [t('commonUi.hours')]: typeof hours === 'number' ? hours.toFixed(2) : hours,
          [t('commonUi.description')]: description,
          [t('pages.reports.colProductionLot')]: referenceProductionTaskId,
          [t('pages.reports.sapTaskId')]: productionTaskId,
          [t('pages.reports.activity')]: activityName,
          [t('commonUi.status')]: status,
          [t('commonUi.employee')]: employee,
          [t('commonUi.notes')]: notes,
          [t('commonUi.version')]: version,
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:L1');

      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!ws[cellAddress]) continue;
        ws[cellAddress].s = {
          font: { bold: true, sz: 12, name: 'Calibri', color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "2E75B6" }, patternType: "solid" },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }

      ws['!cols'] = [];
      for (let C = range.s.c; C <= range.e.c; ++C) {
        let maxWidth = 15;
        for (let R = range.s.r; R <= range.e.r; ++R) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = ws[cellAddress];
          if (cell && cell.v) {
            let cellLength = String(cell.v).length;
            if (C === 0) cellLength = Math.min(cellLength + 2, 15);
            if (C === 4) cellLength = Math.min(cellLength + 5, 40);
            if (C === 5 || C === 6) cellLength = Math.min(cellLength + 3, 25);
            maxWidth = Math.max(maxWidth, Math.min(cellLength, 50));
          }
        }
        ws['!cols'].push({ wch: maxWidth + 2 });
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('pages.timeEntry.pageTitle'));

      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `time_entries_${timestamp}.xlsx`;

      XLSX.writeFile(wb, filename);

      toast.success(t('pages.timeEntry.toastExported', { count: exportData.length }), { duration: 3000, id: exportToastId });
    } catch (error) {
      console.error(t('pages.timeEntry.toastExportFailed'), error);
      toast.error(t('pages.timeEntry.toastExportFailed'), { duration: 3000, id: exportToastId });
    }
  }, [employeeUUId, debouncedSearch, filterDateFrom, filterDateTo, filterStatus, employeeName, t]);

  const calculateHours = useCallback((start, end) => {
    if (!start) return 0;
    if (!end) {
      const now = new Date();
      const s = new Date(`2000-01-01T${start}`);
      const n = new Date(`2000-01-01T${now.toTimeString().slice(0, 5)}`);
      const diff = (n - s) / (1000 * 60 * 60);
      return diff > 0 ? Math.round(diff * 10) / 10 : 0;
    }
    if (start >= end) return 0;
    const s = new Date(`2000-01-01T${start}`);
    const e = new Date(`2000-01-01T${end}`);
    const diff = (e - s) / (1000 * 60 * 60);
    return diff > 0 ? Math.round(diff * 10) / 10 : 0;
  }, []);

  const handleBarcodeSearch = useCallback(async () => {
    const code = barcode.trim();
    if (!code) return;
    setBarcodeLoading(true);
    setBarcodeError('');
    setBarcodeTask(null);
    setSelectedActivity(null);
    setTaskHistory([]);
    setContinueFromEntry(null);
    try {
      const res = await getProductionTaskByBarcode(code);
      if (res.isSuccess && res.data) {
        setBarcodeTask(res.data);
        setTimerDescription(`${t('pages.reports.task')}: ${res.data.productionTaskId || res.data.id}`);
        const taskId = res.data.productionTaskId || res.data.id;
        if (taskId) {
          try {
            const histRes = await getTimeEntryHistoryByTask(taskId);
            if (histRes.isSuccess && histRes.data?.length > 0) setTaskHistory(histRes.data);
          } catch { }
        }
      } else { setBarcodeError(res.message || t('pages.timeEntry.noTasksBody')); }
    } catch { setBarcodeError(t('commonUi.error')); }
    finally { setBarcodeLoading(false); }
  }, [barcode, t]);

  // --------------------------------------------------------------------
  // handleStartTimer – STORES LOCAL START TIME
  // --------------------------------------------------------------------
  const handleStartTimer = useCallback(async (taskId, accumulatedSeconds = 0, parentTimeEntryId = null, activityOverride = undefined, barcodeTaskOverride = undefined, activitiesOverride = undefined, descriptionOverride = undefined) => {
    try {
      const now = new Date();
      const effectiveActivity = activityOverride !== undefined ? activityOverride : selectedActivity;
      const effectiveBarcodeTask = barcodeTaskOverride !== undefined ? barcodeTaskOverride : barcodeTask;
      if (effectiveActivity?.processingStatusCode === '3') {
        showNotification(t('pages.timeEntry.noTasksBody'), 'error');
        return;
      }
      if (effectiveBarcodeTask?.processingStatusCode === '3') {
        showNotification(t('pages.timeEntry.noTasksBody'), 'error');
        return;
      }
      const desc = descriptionOverride || timerDescription || (effectiveBarcodeTask ? `${t('pages.reports.task')}: ${effectiveBarcodeTask.productionTaskId || effectiveBarcodeTask.id}` : '');
      const payload = {
        employeeUUId, employeeName, timeTypeCode: 'LABOR', date: now.toISOString(),
        referenceProductionTaskId: taskId || effectiveBarcodeTask?.id || '', description: desc,
        productionTaskId: effectiveBarcodeTask?.productionTaskId || effectiveBarcodeTask?.id || '',
        barcodeImage: effectiveBarcodeTask?.barcodeImage || '', barcodeBase64: effectiveBarcodeTask?.barcodeBase64 || '',
        activityId: effectiveActivity?.activityId || null, activityName: effectiveActivity?.description || effectiveActivity?.activityId || null,
        accumulatedSeconds: accumulatedSeconds || 0, parentTimeEntryId: parentTimeEntryId || null,
      };
      const tempId = `temp-${Date.now()}`;
      const nowMs = Date.now();
      setLocalStartTime(tempId, nowMs);
      setRunningEntries(prev => [...prev, {
        id: tempId, startedAt: now.toISOString(), createdAt: now.toISOString(), description: desc, isRunning: true,
        referenceProductionTaskId: taskId || effectiveBarcodeTask?.id || '',
        activityId: effectiveActivity?.activityId || null, activityName: effectiveActivity?.description || effectiveActivity?.activityId || null,
        accumulatedSeconds: accumulatedSeconds || 0,
        _activities: activitiesOverride || [],
      }]);
      setShowModal(false);
      setShowPreviousSessions(false);
      setContinueFromEntry(null);
      const res = await apiStartTimeEntry(payload);
      if (res.isSuccess && res.data?.hasConflict) {
        setRunningEntries(prev => prev.filter(e => e.id !== tempId));
        removeLocalStartTime(tempId);
        setActivityConflict({ conflictingEntry: res.data.conflictingEntry, pendingPayload: payload, pendingMeta: { effectiveBarcodeTask, sapTaskId: effectiveBarcodeTask?.productionTaskId || effectiveBarcodeTask?.id || '' } });
        return;
      }
      if (res.isSuccess) {
        toast.success('Timer Started');
        const realEntry = res.data.startedEntry;
        if (realEntry && realEntry.id) {
          const storedStartMs = getLocalStartTime(tempId);
          if (storedStartMs) {
            setLocalStartTime(realEntry.id, storedStartMs);
            removeLocalStartTime(tempId);
          }
        }
        const sapTaskId = effectiveBarcodeTask?.productionTaskId || effectiveBarcodeTask?.id || '';
        if (sapTaskId) {
          try {
            const sapRes = await startProductionTask(sapTaskId);
            if (sapRes?.isSuccess) showNotification(`${t('commonUi.success')} ${t('pages.timeEntry.modalStartTitle')}`);
            else showNotification(`${t('pages.timeEntry.modalStartTitle')} ${t('commonUi.error')}: ${sapRes?.message || t('commonUi.error')}`, 'warning');
          } catch (sapErr) { console.error('SAP start production task error:', sapErr); showNotification(`${t('pages.timeEntry.modalStartTitle')} ${t('commonUi.error')}: ${sapErr.message || t('commonUi.error')}`, 'warning'); }
        } else { showNotification(t('pages.timeEntry.modalStartTitle')); }
        await fetchData();
      } else {
        setRunningEntries(prev => prev.filter(e => e.id !== tempId));
        removeLocalStartTime(tempId);
        showNotification(res.message || t('commonUi.error'), 'error');
      }
    } catch (err) {
      setRunningEntries(prev => prev.filter(e => !String(e.id).startsWith('temp-')));
      showNotification(t('commonUi.error'), 'error');
    }
  }, [employeeUUId, employeeName, barcodeTask, timerDescription, selectedActivity, showNotification, fetchData, t]);

  const handleContinueEntry = useCallback((entry) => {
    if (!entry) return;
    if (entry.isRunning) {
      showNotification(t('navbar.timerRunning'));
      return;
    }

    const isCompleted = entry.processingStatusCode === 'Finished' || entry.processingStatusCode === '3' ||
      entry.activityProcessingStatusCode === 'Finished' || entry.activityProcessingStatusCode === '3';
    if (isCompleted) {
      showNotification(t('pages.timeEntry.noTasksBody'), 'error');
      return;
    }

    const taskId = entry.referenceProductionTaskId || entry.productionTaskId || '';
    if (!taskId) {
      showNotification(t('commonUi.error'), 'error');
      return;
    }

    const accumulatedSeconds = Math.round((parseFloat(entry.hours) || 0) * 3600) + (entry.accumulatedSeconds || 0);
    const activity = entry.activityId
      ? {
        activityId: entry.activityId,
        description: entry.activityName || entry.activityId,
        activityDescription: entry.activityName || entry.activityId,
        processingStatusCode: entry.activityProcessingStatusCode || null,
      }
      : null;
    const taskFromEntry = {
      id: entry.referenceProductionTaskId || taskId,
      productionTaskId: entry.productionTaskId || taskId,
      productionLotId: entry.referenceProductionTaskId || '',
      processingStatusCode: entry.processingStatusCode || null,
      barcodeImage: entry.barcodeImage || '',
      barcodeBase64: entry.barcodeBase64 || '',
    };

    handleStartTimer(
      taskId,
      accumulatedSeconds,
      entry.id,
      activity,
      taskFromEntry,
      [],
      entry.description || `${t('pages.reports.task')}: ${taskId}`
    );
  }, [handleStartTimer, showNotification, t]);

  const handleStopAndStart = useCallback(async () => {
    if (!activityConflict) return;
    const { conflictingEntry, pendingPayload, pendingMeta } = activityConflict;
    setActivityConflict(null);
    try {
      const res = await apiStopAndStart(conflictingEntry.id, pendingPayload);
      if (res.isSuccess && !res.data?.hasConflict) {
        const sapTaskId = pendingMeta?.sapTaskId || '';
        if (sapTaskId) { try { await startProductionTask(sapTaskId); } catch { } }
        showNotification(t('pages.timeEntry.stopAndStart'));
        await fetchData();
      } else { showNotification(res.message || t('commonUi.error'), 'error'); }
    } catch (err) { showNotification(t('commonUi.error'), 'error'); }
  }, [activityConflict, showNotification, fetchData, t]);

  const handleStopTimer = useCallback(async (entryId, notes = '', activityId = null, activityName = null) => {
    if (!entryId) return;
    try {
      const res = await apiStopTimeEntry(entryId, notes || null, activityId, activityName);
      if (res.isSuccess) {
        toast.success('Timer Stopped');
        showNotification(t('commonUi.success'));
        const savedPause = JSON.parse(sessionStorage.getItem(pauseStorageKey) || '{}');
        delete savedPause[entryId];
        sessionStorage.setItem(pauseStorageKey, JSON.stringify(savedPause));
        removeLocalStartTime(entryId);
        setRunningEntries(prev => prev.filter(e => e.id !== entryId));
        await fetchData();
      } else { showNotification(res.message || t('commonUi.error'), 'error'); }
    } catch (err) { showNotification(t('commonUi.error'), 'error'); }
  }, [showNotification, pauseStorageKey, fetchData, t]);

  const handleCompleteTask = useCallback(async (confirmData) => {
    const taskId = typeof confirmData === 'object' ? confirmData.taskId : confirmData;
    const activityId = typeof confirmData === 'object' ? confirmData.activityId : null;
    if (!taskId) return;
    setCompleting(true);
    try {
      const res = await completeProductionTask(taskId, activityId);
      if (res?.isSuccess) { showNotification(res?.message || t('enums.completed')); await fetchData(); }
      else { showNotification(res?.message || t('commonUi.error'), 'error'); }
    } catch (err) { console.error('Complete production task error:', err); showNotification(err.message || t('commonUi.error'), 'error'); }
    finally { setCompleting(false); setShowCompleteConfirm(null); }
  }, [showNotification, fetchData, t]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = (dateStr || '').slice(0, 10);
    const today = todayStr();
    const yest = new Date(); yest.setDate(yest.getDate() - 1);
    const yesterdayStr = yest.toISOString().split('T')[0];
    if (d === today) return t('common.today');
    if (d === yesterdayStr) return t('common.yesterday');
    return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };
  const formatTime = (t) => t || '-';
  const formatHoursDisplay = (entry) => {
    const h = parseFloat(entry.hours) || 0;
    if (h === 0) return { value: '0', unit: t('commonUi.hrs') };
    if (h < 1) return { value: Math.round(h * 60).toString(), unit: 'm' };
    const hrs = Math.floor(h);
    const mins = Math.round((h - hrs) * 60);
    if (mins === 0) return { value: hrs.toString(), unit: t('commonUi.hrs') };
    return { value: `${hrs}h ${mins}`, unit: 'm' };
  };

  const validateForm = useCallback(() => {
    const errors = {};
    if (!formData.date) errors.date = t('pages.timeEntry.toastSyncFailed');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: null }));
  }, [formErrors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const hours = calculateHours(formData.startTime, formData.endTime);
      if (editingEntry) {
        const payload = { startTime: formData.startTime || null, endTime: formData.endTime || null, hours, description: formData.description || null, isRunning: false };
        const res = await apiUpdateTimeEntry(editingEntry.id, payload);
        if (res.isSuccess) showNotification(t('commonUi.success'));
        else { showNotification(res.message || t('commonUi.error'), 'error'); setSubmitting(false); return; }
      } else {
        const selectedTask = productionTasks.find(t => t.id === formData.referenceProductionTaskId);
        const payload = {
          employeeUUId, employeeName, timeTypeCode: formData.timeTypeCode || 'LABOR', date: new Date(formData.date).toISOString(),
          startTime: formData.startTime || null, endTime: formData.endTime || null, hours, description: formData.description || null,
          referenceProductionTaskId: formData.referenceProductionTaskId || null,
          productionTaskId: selectedTask?.productionTaskId || formData.referenceProductionTaskId || null,
          barcodeImage: selectedTask?.barcodeImage || null, barcodeBase64: selectedTask?.barcodeBase64 || null, isRunning: false,
        };
        const res = await apiCreateTimeEntry(payload);
        if (res.isSuccess) showNotification(t('commonUi.success'));
        else { showNotification(res.message || t('commonUi.error'), 'error'); setSubmitting(false); return; }
      }
      closeModal();
      await fetchData();
    } catch (err) { showNotification(err.message || t('commonUi.error'), 'error'); }
    finally { setSubmitting(false); }
  };

  const handleEdit = useCallback((entry) => {
    setEditingEntry(entry);
    setFormData({
      date: (entry.date || '').slice(0, 10), startTime: entry.startTime || '', endTime: entry.endTime || '',
      description: entry.description || '', referenceProductionTaskId: entry.referenceProductionTaskId || '', timeTypeCode: entry.timeTypeCode || 'LABOR',
    });
    setFormErrors({});
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingEntry(null);
    setFormErrors({});
    setFormData({ date: todayStr(), startTime: '', endTime: '', description: '', referenceProductionTaskId: '', timeTypeCode: 'LABOR' });
    setBarcode(''); setBarcodeTask(null); setBarcodeError(''); setTimerDescription(''); setSelectedActivity(null); setTaskHistory([]); setShowPreviousSessions(false); setContinueFromEntry(null);
  }, [todayStr]);

  const handleTaskPickerStartTimer = useCallback((task, accumulatedSeconds = 0, parentTimeEntryId = null, activity = null) => {
    const taskId = task?.productionLotId || task?.id || '';
    const desc = task?.mainOutputProductDescription || task?.title || `${taskId}`;
    setTimerDescription(desc);
    const freshBarcodeTask = {
      id: taskId, productionTaskId: task?.dbProductionTasks[0]?.productionTaskId || '', productionLotId: task?.productionLotId || '',
      barcodeBase64: task?.barcodeBase64 || '', barcodeImage: task?.barcodeImage || '',
    };
    setBarcodeTask(freshBarcodeTask);
    const confirmationGroups = task?.dbConfirmationGroups || task?.confirmationGroups || [];
    const taskActivities = [];
    const seen = new Set();
    confirmationGroups.forEach(g => { (g.dbActivities || []).forEach(a => { if (a.activityId && !seen.has(a.activityId)) { seen.add(a.activityId); taskActivities.push({ activityId: a.activityId, activityUUID: a.activityUUID, activityDescription: a.activityDescription || a.activityId, operationId: a.operationId, plannedDuration: a.plannedDuration }); } }); });
    if (activity) setSelectedActivity(activity);
    handleStartTimer(taskId, accumulatedSeconds, parentTimeEntryId, activity, freshBarcodeTask, taskActivities, desc);
  }, [handleStartTimer, t]);

  const openStopModal = useCallback(async (entryId) => {
    const entry = runningEntries.find(e => e.id === entryId);
    if (entry?.activityId) { setStopActivity({ activityId: entry.activityId, activityDescription: entry.activityName || entry.activityId }); } else { setStopActivity(null); }
    if (entry?._activities?.length > 0) { setStopModalActivities(entry._activities); setShowStopConfirm(entryId); return; }
    const taskId = entry?.referenceProductionTaskId || entry?.productionTaskId;
    if (!taskId) { setStopModalActivities([]); setShowStopConfirm(entryId); return; }
    setStopModalLoading(true);
    try {
      const res = await getProductionTaskById(taskId);
      const task = res?.data || res;
      const groups = task?.dbConfirmationGroups || task?.confirmationGroups || [];
      const acts = [];
      const seen = new Set();
      groups.forEach(g => { (g.dbActivities || []).forEach(a => { if (a.activityId && !seen.has(a.activityId)) { seen.add(a.activityId); acts.push({ activityId: a.activityId, activityDescription: a.activityDescription || a.activityId }); } }); });
      setStopModalActivities(acts);
    } catch { setStopModalActivities([]); }
    finally { setStopModalLoading(false); setShowStopConfirm(entryId); }
  }, [runningEntries]);

  // Rest of the JSX remains the same as your original (too long to repeat, but the export button is updated)
  // The export button in the JSX should call handleExport

  return (
    <div className="space-y-3 sm:space-y-6 animate-fade-in min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 -m-3 p-3 sm:-m-6 sm:p-6">
      {/* Notification Toast */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-5 py-4 rounded-xl shadow-lg animate-slide-up ${notification.type === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
            }`}
        >
          {notification.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
          <span className="font-medium">{notification.message}</span>
          <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-70">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* SAP Sync Overlay - keep as is */}
      {syncLoading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-10 max-w-md w-full mx-4 flex flex-col items-center text-center border border-gray-200 dark:border-gray-700 animate-fade-in">
            <div className="relative w-24 h-24 mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-emerald-200 dark:border-emerald-900 animate-ping opacity-30" />
              <div className="absolute inset-2 rounded-full border-4 border-teal-300 dark:border-teal-700 animate-spin" style={{ borderTopColor: 'transparent' }} />
              <div className="absolute inset-5 rounded-full border-4 border-emerald-400 dark:border-emerald-600 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s', borderBottomColor: 'transparent' }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <Database className="w-8 h-8 text-emerald-600 dark:text-emerald-400 animate-pulse" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('common.loading')}</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
              {t('common.loading')}
            </p>
            <div className="mt-5 flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>{t('common.loading')}</span>
            </div>
            <button
              onClick={handleContinueInBackground}
              className="mt-6 px-6 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-semibold transition-all duration-200 hover:shadow-md"
            >
              {t('common.continue')}
            </button>
          </div>
        </div>
      )}

      {/* Full page loader while fetching initial data */}
      {loading ? (
        <TimeEntryListSkeleton rows={10} />
      ) : (
        <>
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="hidden sm:block">
                <AnimatedClock size={64} compact showDigital={false} glowColor="#3b82f6" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">{t('pages.timeEntry.pageTitle')}</h1>
                <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm mt-0.5">{t('pages.dashboard.timeEntryOverviewSub')}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {user?.role !== UserRole.Employee && (
              <button
                onClick={handleSyncFromSap}
                disabled={syncLoading || syncInBackground}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 text-xs sm:text-sm"
              >
                {syncLoading || syncInBackground ? (
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                ) : (
                  <Database className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
                <span className="hidden sm:inline">
                  {syncLoading ? t('common.loading') : syncInBackground ? t('common.loading') : t('pages.sync.syncService')}
                </span>
                <span className="sm:hidden">{syncLoading ? t('common.loading') : syncInBackground ? t('common.loading') : t('pages.sync.syncService')}</span>
              </button>
              )}
              <button
                onClick={() => loadFullTasks()}
                disabled={taskPickerLoading}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 text-xs sm:text-sm"
              >
                <Layers className={`w-4 h-4 sm:w-5 sm:h-5 ${taskPickerLoading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{taskPickerLoading ? t('common.loading') : t('pages.dashboard.statProductionTasks')}</span>
                <span className="sm:hidden">{taskPickerLoading ? t('common.loading') : t('pages.dashboard.statProductionTasks')}</span>
                {!taskPickerLoading && taskCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-white/25 rounded-full text-xs font-bold">{taskCount}</span>
                )}
              </button>
              {/* View mode toggle */}
              <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1 gap-0.5">
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${viewMode === 'list'
                    ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                >
                  <List className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('commonUi.list')}</span>
                </button>
                <button
                  onClick={() => setViewMode('timeline')}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${viewMode === 'timeline'
                    ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                >
                  <Sparkles className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('pages.dashboard.recentTimeEntries')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Running Timer Banners */}
          {runningEntries.length > 0 && (
            <div className="space-y-3">
              {runningEntries.map((entry) => {
                const isPaused = pausedMap[entry.id] !== undefined;

                // Determine if this is a restart (has prior stopped sessions)
                const hasPriorSessions = entry.activityId || entry.activityName
                  ? timeEntries.filter(e =>
                      (entry.activityId && e.activityId === entry.activityId) ||
                      (entry.activityName && e.activityName === entry.activityName)
                    ).length > 0
                  : false;

                const startLabel = hasPriorSessions ? 'Restarted' : 'Started';
                const localStart = entry.startedAt
                  ? new Date(entry.startedAt).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                  : '';

                return (
                  <div
                    key={entry.id}
                    className={`rounded-2xl p-4 sm:p-5 shadow-lg text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-4 ${isPaused
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500'
                      : 'bg-gradient-to-r from-teal-500 to-emerald-500'
                      }`}
                  >
                    <div className="flex items-center gap-3 sm:gap-4 w-full md:w-auto">
                      <div className={`p-2 sm:p-3 bg-white/20 rounded-xl flex-shrink-0 ${isPaused ? '' : 'animate-pulse'}`}>
                        {isPaused ? <Pause className="w-5 h-5 sm:w-7 sm:h-7" /> : <Timer className="w-5 h-5 sm:w-7 sm:h-7" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-xs sm:text-sm font-medium ${isPaused ? 'text-amber-100' : 'text-teal-100'}`}>
                          {isPaused ? t('commonUi.paused') : t('navbar.timerRunning')}
                        </p>
                        <p className="text-2xl sm:text-4xl font-bold font-mono tracking-wider">{elapsedMap[entry.id] || '00:00:00'}</p>

                        {/* Display start label and local time */}
                        {localStart && (
                          <p className={`text-xs sm:text-sm mt-1 ${isPaused ? 'text-amber-100' : 'text-teal-100'}`}>
                            {startLabel}: {localStart}
                          </p>
                        )}

                        {entry.description && (
                          <p className={`text-xs sm:text-sm mt-1 truncate ${isPaused ? 'text-amber-100' : 'text-teal-100'}`}>
                            {entry.description}
                          </p>
                        )}

                        {/* Show activity info */}
                        {(entry.activityName || entry.activityId) && (
                          <div className="inline-flex items-center gap-1.5 mt-1.5 px-2 sm:px-2.5 py-1 bg-white/20 rounded-lg">
                            <Layers className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                            <span className="text-[10px] sm:text-xs font-medium truncate">
                              {entry.activityName || entry.activityId}
                            </span>
                            {/* Show version info if available */}
                            {entry.version && entry.version > 1 && (
                              <span className="text-[8px] bg-white/30 px-1 rounded">
                                v{entry.version}
                              </span>
                            )}
                          </div>
                        )}
                        {!entry.activityName && !entry.activityId && (
                          <div className="inline-flex items-center gap-1.5 mt-1.5 px-2 sm:px-2.5 py-1 bg-white/20 rounded-lg opacity-70">
                            <Layers className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                            <span className="text-[10px] sm:text-xs font-medium">{t('commonUi.all')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 w-full md:w-auto">
                      <button
                        onClick={() => openStopModal(entry.id)}
                        disabled={stopModalLoading}
                        className="flex items-center justify-center gap-1.5 sm:gap-2 flex-1 md:flex-none px-4 sm:px-6 py-2.5 sm:py-3 bg-white dark:bg-gray-800 text-red-600 dark:text-red-400 rounded-xl font-bold hover:bg-red-50 dark:hover:bg-gray-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 text-sm sm:text-base"
                      >
                        {stopModalLoading ? <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" /> : <Square className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />}{' '}
                        {t('commonUi.stop')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Weekly Summary Cards */}
          {weeklySummary && (
            <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl sm:rounded-2xl shadow-md border border-blue-200 dark:border-blue-800 p-2 sm:p-5 hover:shadow-lg transition-all hover:scale-[1.02] cursor-default">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-blue-600 dark:text-blue-400 text-[10px] sm:text-sm">{t('pages.dashboard.weeklyHours')}</p>
                    <p className="font-bold text-blue-700 dark:text-blue-300 text-lg sm:text-3xl mt-0.5 sm:mt-1">{weeklySummary.totalHours}{t('commonUi.hrs')}</p>
                  </div>
                  <div className="bg-blue-200/50 dark:bg-blue-800/50 backdrop-blur p-1.5 sm:p-3 rounded-lg sm:rounded-xl">
                    <TrendingUp className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <div className="text-blue-500 dark:text-blue-400 mt-1 sm:mt-3 text-[9px] sm:text-xs">
                  <span className="font-bold text-blue-700 dark:text-blue-300">{weeklySummary.entriesCount}</span> {t('commonUi.entries')}
                </div>
              </div>
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900/30 dark:to-teal-900/30 rounded-xl sm:rounded-2xl shadow-md border border-teal-200 dark:border-teal-800 p-2 sm:p-5 hover:shadow-lg transition-all hover:scale-[1.02] cursor-default">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-teal-600 dark:text-teal-400 text-[10px] sm:text-sm">{t('commonUi.entries')}</p>
                    <p className="font-bold text-teal-700 dark:text-teal-300 text-lg sm:text-3xl mt-0.5 sm:mt-1">{timeEntries.length}</p>
                  </div>
                  <div className="bg-teal-200/50 dark:bg-teal-800/50 backdrop-blur p-1.5 sm:p-3 rounded-lg sm:rounded-xl">
                    <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-teal-600 dark:text-teal-400" />
                  </div>
                </div>
                <div className="text-teal-500 dark:text-teal-400 mt-1 sm:mt-3 text-[9px] sm:text-xs">
                  <span className="text-teal-700 dark:text-teal-300">{t('commonUi.total')}</span>
                </div>
              </div>
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/30 dark:to-indigo-900/30 rounded-xl sm:rounded-2xl shadow-md border border-indigo-200 dark:border-indigo-800 p-2 sm:p-5 hover:shadow-lg transition-all hover:scale-[1.02] cursor-default">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-indigo-600 dark:text-indigo-400 text-[10px] sm:text-sm">{t('commonUi.average')}</p>
                    <p className="font-bold text-indigo-700 dark:text-indigo-300 text-lg sm:text-3xl mt-0.5 sm:mt-1">
                      {weeklySummary.entriesCount > 0 ? (weeklySummary.totalHours / weeklySummary.entriesCount).toFixed(1) : 0}{t('commonUi.hrs')}
                    </p>
                  </div>
                  <div className="bg-indigo-200/50 dark:bg-indigo-800/50 backdrop-blur p-1.5 sm:p-3 rounded-lg sm:rounded-xl">
                    <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                </div>
                <div className="text-indigo-500 dark:text-indigo-400 mt-1 sm:mt-3 text-[9px] sm:text-xs">
                  <span className="text-indigo-700 dark:text-indigo-300">{weeklySummary.entriesCount} {t('commonUi.entries')}</span>
                </div>
              </div>
              <div
                onClick={() => !taskPickerLoading && taskCount > 0 && loadFullTasks()}
                className={`bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800/50 rounded-xl sm:rounded-2xl shadow-md border border-slate-200 dark:border-slate-700 p-2 sm:p-5 hover:shadow-lg transition-all hover:scale-[1.02] ${taskCount > 0 && !taskPickerLoading ? 'cursor-pointer ring-0 hover:ring-2 hover:ring-blue-300 dark:hover:ring-blue-600' : 'cursor-default'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-600 dark:text-slate-400 text-[10px] sm:text-sm">{t('pages.dashboard.statProductionTasks')}</p>
                    <p className="font-bold text-slate-700 dark:text-slate-300 text-lg sm:text-3xl mt-0.5 sm:mt-1">
                      {taskPickerLoading ? '...' : taskCount}
                    </p>
                  </div>
                  <div className="bg-slate-200/50 dark:bg-slate-700/50 backdrop-blur p-1.5 sm:p-3 rounded-lg sm:rounded-xl">
                    <Calendar className={`w-4 h-4 sm:w-6 sm:h-6 text-slate-600 dark:text-slate-400 ${taskPickerLoading ? 'animate-spin' : ''}`} />
                  </div>
                </div>
                <div className="text-slate-500 dark:text-slate-400 mt-1 sm:mt-3 text-[9px] sm:text-xs">
                  <span className="text-slate-700 dark:text-slate-300">{taskPickerLoading ? t('common.loading') : taskCount > 0 ? t('commonUi.browse') : t('commonUi.none')}</span>
                </div>
              </div>
            </div>
          )}

          {/* My Assigned Production Tasks */}
          {myAssignedTasks.length > 0 && viewMode !== 'timeline' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                      <CheckCircle className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                    </div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200">{t('sidebar.myTasks')}</h3>
                    <span className="text-xs px-2 py-0.5 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 rounded-full font-medium">
                      {myAssignedTasks.length}
                    </span>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-gray-50 dark:divide-gray-700 max-h-72 overflow-y-auto">
                {myAssignedTasks.map((task) => (
                  <div
                    key={task.id}
                    className="px-4 sm:px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                          {task.productionLotName || task.productionLotId || t('pages.dashboard.statProductionTasks')}
                        </p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${getStatusBadgeColor(task.status)}`}>
                          {getStatusName(task.status)}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${getPriorityBadgeColor(task.priority)}`}>
                          {getPriorityName(task.priority)}
                        </span>
                      </div>
                      {task.description && <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{task.description}</p>}
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                      {task.dueDate && (
                        <span className={task.isOverdue ? 'text-red-600 font-medium' : ''}>
                          {t('commonUi.due')}: {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                      )}
                      <span>{task.actualHours?.toFixed(1) || 0}{t('commonUi.hrs')} {t('commonUi.logged')}</span>
                      <button
                        onClick={() => {
                          setEditingEntry(null);
                          setFormData({
                            date: todayStr(),
                            startTime: nowTimeStr(),
                            endTime: '',
                            description: task.productionLotName || task.description || '',
                            referenceProductionTaskId: task.productionLotId || '',
                            timeTypeCode: 'LABOR',
                          });
                          setFormErrors({});
                          setShowModal(true);
                        }}
                        className="px-3 py-1.5 bg-violet-100 text-violet-700 rounded-lg text-xs font-semibold hover:bg-violet-200 transition-colors"
                      >
                        {t('commonUi.log')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search and Filters - only show in list view */}
          {viewMode === 'list' && (
            <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
              <div className="flex flex-col md:flex-row gap-2 sm:gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('pages.timeEntry.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-sap-blue focus:border-transparent transition-all text-sm sm:text-base text-gray-900 dark:text-gray-100 dark:bg-gray-700 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 border rounded-xl transition-colors ${showFilters
                      ? 'border-sap-blue bg-blue-50 dark:bg-blue-900/30 text-sap-blue'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                  >
                    <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="hidden sm:inline font-medium">{t('pages.reports.filters')}</span>
                  </button>
                  <button
                    onClick={handleExport}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300"
                  >
                    <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="hidden sm:inline font-medium">{t('commonUi.export')}</span>
                  </button>
                </div>
              </div>
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-1 md:grid-cols-4 gap-4 animate-fade-in">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{t('pages.reports.startDate')}</label>
                    <input
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-sap-blue focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{t('pages.reports.endDate')}</label>
                    <input
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-sap-blue focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{t('commonUi.status')}</label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-sap-blue focus:border-transparent dark:bg-gray-700 dark:text-gray-100 cursor-pointer"
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        setFilterDateFrom('');
                        setFilterDateTo('');
                        setFilterStatus('');
                        setSearchTerm('');
                      }}
                      className="px-4 py-2.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium"
                    >
                      {t('commonUi.clearAll')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Timeline View */}
          {viewMode === 'timeline' && (
            <ConstellationTimeline
              entries={timeEntries}
              elapsedMap={elapsedMap}
              onContinueEntry={handleContinueEntry}
              isLoadingMore={entriesPageLoading}
              hasMore={entriesPage < entriesTotalPages}
              onLoadMore={handleLoadMoreTimeline}
            />
          )}

          {/* Time Entries Table */}
          {viewMode === 'list' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-lg overflow-hidden">
              {timeEntries.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-80 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-800 dark:to-gray-900">
                  <div className="relative mb-5">
                    <div className="w-24 h-24 bg-gradient-to-br from-indigo-400 to-purple-500 dark:from-indigo-500 dark:to-purple-600 rounded-full flex items-center justify-center shadow-xl shadow-indigo-200 dark:shadow-indigo-900/50">
                      <Clock className="w-11 h-11 text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-7 h-7 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-md">
                      <Plus className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-xl font-bold text-gray-800 dark:text-gray-100">{t('pages.dashboard.noEntriesYet')}</p>
                  {<p className="text-sm text-gray-400 dark:text-gray-500 mt-2">{t('pages.dashboard.noEntriesHint')}</p>}
                </div>
              ) : (
                <>
                  {/* Mobile card layout (< md) - keep as is from your original */}
                  <div className="block md:hidden divide-y divide-gray-100 dark:divide-gray-700">
                    {timeEntries.map((entry) => {
                      const dateStr = (entry.date || '').slice(0, 10);
                      const isToday = dateStr === todayStr();
                      const isRunning = entry.isRunning;
                      const isFinished = entry.processingStatusCode === 'Finished';
                      return (
                        <div key={entry.id} className="p-4 space-y-3">
                          {/* Top row: date + status + hours */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <div
                                className={`p-1.5 rounded-lg flex-shrink-0 ${isRunning
                                  ? 'bg-gradient-to-br from-teal-400 to-emerald-500'
                                  : isToday
                                    ? 'bg-teal-500'
                                    : 'bg-blue-400'
                                  }`}
                              >
                                <Calendar className="w-3.5 h-3.5 text-white" />
                              </div>
                              <div className="min-w-0">
                                <span
                                  className={`font-semibold text-sm block ${isToday || isRunning ? 'text-teal-600 dark:text-teal-400' : 'text-gray-900 dark:text-gray-100'
                                    }`}
                                >
                                  {formatDate(entry.date)}
                                </span>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">{dateStr}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {isRunning ? (
                                <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-teal-100 to-emerald-100 dark:from-teal-900/40 dark:to-emerald-900/40 rounded-full border border-teal-200 dark:border-teal-800">
                                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse"></span>
                                  <span className="text-[10px] font-bold text-teal-700 dark:text-teal-400">{t('enums.in_progress').toUpperCase()}</span>
                                </div>
                              ) : entry.processingStatusCode ? (
                                <div
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${isFinished
                                    ? 'bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800'
                                    : entry.processingStatusCode === 'In Process'
                                      ? 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800'
                                      : entry.processingStatusCode === 'Not Started'
                                        ? 'bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                                        : 'bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800'
                                    }`}
                                >
                                  <div
                                    className={`w-1.5 h-1.5 rounded-full ${isFinished
                                      ? 'bg-green-500'
                                      : entry.processingStatusCode === 'In Process'
                                        ? 'bg-yellow-500'
                                        : entry.processingStatusCode === 'Not Started'
                                          ? 'bg-gray-500'
                                          : 'bg-blue-500'
                                      }`}
                                  ></div>
                                  <span
                                    className={`text-[10px] font-bold ${isFinished
                                      ? 'text-green-700 dark:text-green-400'
                                      : entry.processingStatusCode === 'In Process'
                                        ? 'text-yellow-700 dark:text-yellow-400'
                                        : entry.processingStatusCode === 'Not Started'
                                          ? 'text-gray-700 dark:text-gray-400'
                                          : 'text-blue-700 dark:text-blue-400'
                                      }`}
                                  >
                                    {entry.processingStatusCode === 'Finished' ? t('enums.completed') :
                                      entry.processingStatusCode === 'In Process' ? t('enums.in_progress') :
                                        entry.processingStatusCode === 'Not Started' ? t('commonUi.none') :
                                          entry.processingStatusCode}
                                  </span>
                                </div>
                              ) : null}
                              {isRunning ? (
                                <span className="text-sm font-bold text-teal-600 flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-ping"></span>
                                  {(() => {
                                    const elapsed = elapsedMap[entry.id];
                                    if (!elapsed) return '0m';
                                    const parts = elapsed.split(':');
                                    const hrs = parseInt(parts[0], 10);
                                    const mins = parseInt(parts[1], 10);
                                    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
                                  })()}
                                </span>
                              ) : (
                                <span className="text-sm font-bold text-indigo-600">
                                  {formatHoursDisplay(entry).value}
                                  <span className="text-xs text-indigo-400 ml-0.5">{formatHoursDisplay(entry).unit}</span>
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Time range */}
                          <div className="flex items-center gap-3 flex-wrap">
                            {isRunning ? (
                              <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/30 rounded-full border border-teal-200 dark:border-teal-700">
                                <span className="text-teal-600 font-semibold text-xs">{formatLocalStartTime(entry)}</span>
                                <span className="text-teal-300 text-xs">&rarr;</span>
                                <Timer className="w-3 h-3 text-teal-500" />
                                <span className="text-teal-500 font-semibold text-xs">{t('enums.in_progress')}</span>
                              </div>
                            ) : (
                              <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 rounded-full border border-blue-100 dark:border-blue-800">
                                <span className="text-blue-600 font-semibold text-xs">{formatLocalStartTime(entry)}</span>
                                <span className="text-blue-300 text-xs">&rarr;</span>
                                <span className="text-indigo-600 font-semibold text-xs">{formatLocalEndTime(entry)}</span>
                              </div>
                            )}
                            <div className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 dark:bg-gray-700/50 rounded-full">
                              <User className="w-3 h-3 text-indigo-500" />
                              <span className="text-xs text-gray-700 dark:text-gray-300 truncate max-w-[100px]">
                                {entry.employeeName || employeeName}
                              </span>
                            </div>
                          </div>

                          {/* Production task + activity */}
                          {entry.referenceProductionTaskId && (
                            <div className="space-y-1.5">
                              <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
                                <span className="text-slate-700 dark:text-slate-300 text-xs font-medium truncate max-w-[200px]">
                                  {entry.referenceProductionTaskId}
                                </span>
                                {entry.version > 1 && (
                                  <span className="px-1 py-0.5 rounded text-[8px] font-bold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400">
                                    v{entry.version}
                                  </span>
                                )}
                                {(entry.notes || entry.description) && (
                                  <button
                                    onClick={() => setNotesViewEntry(entry)}
                                    className="p-0.5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
                                  >
                                    <Eye className="w-3 h-3 text-indigo-500 dark:text-indigo-400" />
                                  </button>
                                )}
                              </div>
                              {entry.productionTaskId && (
                                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 rounded-lg">
                                  <span className="text-purple-600 dark:text-purple-400 text-[10px] font-semibold">{t('pages.reports.taskId')}:</span>
                                  <span className="text-purple-700 dark:text-purple-300 text-[10px] font-medium truncate">
                                    {entry.productionTaskId}
                                  </span>
                                </div>
                              )}
                              {(entry.activityName || entry.activityId) && (
                                <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-lg">
                                  <Layers className="w-2.5 h-2.5 text-indigo-500" />
                                  <span className="text-indigo-600 dark:text-indigo-400 text-[10px] font-medium truncate">
                                    {entry.activityName || entry.activityId}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          {!entry.referenceProductionTaskId && entry.description && (
                            <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
                              <span className="text-slate-700 dark:text-slate-300 text-xs font-medium truncate">{entry.description}</span>
                              {entry.notes && (
                                <button
                                  onClick={() => setNotesViewEntry(entry)}
                                  className="p-0.5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
                                >
                                  <Eye className="w-3 h-3 text-indigo-500 dark:text-indigo-400" />
                                </button>
                              )}
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            {isRunning && (
                              <button
                                onClick={() => {
                                  openStopModal(entry.id);
                                  if (entry.activityId)
                                    setStopActivity({ activityId: entry.activityId, activityDescription: entry.activityName || entry.activityId });
                                  else setStopActivity(null);
                                }}
                                className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg text-xs font-semibold shadow-sm"
                              >
                                <Square className="w-3 h-3 fill-current" /> {t('commonUi.stop')}
                              </button>
                            )}
                            {!isRunning && entry.referenceProductionTaskId && !isFinished && (
                              <button
                                onClick={() =>
                                  setShowCompleteConfirm({
                                    taskId: entry.productionTaskId || entry.referenceProductionTaskId,
                                    activityId: entry.activityId || null,
                                    activityName: entry.activityName || null,
                                  })
                                }
                                className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-xs font-semibold shadow-sm"
                              >
                                <CheckCircle className="w-3 h-3" /> {t('enums.completed')}
                              </button>
                            )}
                            {!isRunning && isFinished && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg text-[10px] font-medium">
                                <CheckCircle className="w-3 h-3" /> {t('enums.completed')}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop table layout (md+) - keep as is from your original */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full table-fixed min-w-[1000px]">
                      <thead>
                        <tr className="bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/40 dark:to-indigo-900/40">
                          <th className="text-left px-4 py-4 text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider w-[160px]">
                            {t('commonUi.date')}
                          </th>
                          <th className="text-left px-4 py-4 text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider w-[140px]">
                            {t('commonUi.time')}
                          </th>
                          <th className="text-left px-4 py-4 text-xs font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-wider w-[90px]">
                            {t('commonUi.hours')}
                          </th>
                          <th className="text-left px-4 py-4 text-xs font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-wider w-[200px]">
                            {t('pages.dashboard.statProductionTasks')}
                          </th>
                          <th className="text-left px-4 py-4 text-xs font-bold text-purple-700 dark:text-purple-300 uppercase tracking-wider w-[110px]">
                            {t('commonUi.status')}
                          </th>
                          <th className="text-left px-4 py-4 text-xs font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-wider w-[140px]">
                            {t('commonUi.user')}
                          </th>
                          <th className="text-center px-4 py-4 text-xs font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-wider w-[120px]">
                            {t('commonUi.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {(() => {
                          // MODIFIED: Group entries by production task ID AND activity ID
                          const groups = [];
                          const groupMap = {};

                          timeEntries.forEach((entry) => {
                            // Create a composite key using both task ID and activity ID
                            const taskKey = entry.referenceProductionTaskId || entry.productionTaskId;
                            const activityKey = entry.activityId || 'no-activity';

                            // Only create a group key if we have a task, otherwise treat as standalone
                            const groupKey = taskKey ? `${taskKey}|${activityKey}` : entry.id;

                            if (!groupMap[groupKey]) {
                              groupMap[groupKey] = {
                                key: groupKey,
                                entries: [],
                                taskId: taskKey,
                                activityId: entry.activityId,
                                activityName: entry.activityName
                              };
                              groups.push(groupMap[groupKey]);
                            }
                            groupMap[groupKey].entries.push(entry);
                          });

                          groups.forEach((g) => {
                            if (g.taskId) {
                              // Sort entries by version (newest first) within the same task+activity group
                              g.entries.sort((a, b) => (b.version || 0) - (a.version || 0));
                            }
                          });

                          const renderEntryRow = (entry, idx, { isChild = false } = {}) => {
                            const dateStr = (entry.date || '').slice(0, 10);
                            const isToday = dateStr === todayStr();
                            return (
                              <tr
                                key={entry.id}
                                className={`group hover:bg-blue-50/50 dark:hover:bg-gray-700/50 transition-all duration-200 ${isChild
                                  ? 'bg-slate-50/80 dark:bg-gray-800/60'
                                  : idx % 2 === 0
                                    ? 'bg-white dark:bg-gray-800'
                                    : 'bg-slate-50/50 dark:bg-gray-800/80'
                                  }`}
                              >
                                {/* DATE */}
                                <td className={`px-4 py-3 ${isChild ? 'pl-8' : ''}`}>
                                  <div className="flex items-center gap-2">
                                    <div
                                      className={`p-1.5 rounded-lg shadow-sm flex-shrink-0 ${entry.isRunning
                                        ? 'bg-gradient-to-br from-teal-400 to-emerald-500'
                                        : isToday
                                          ? 'bg-teal-500'
                                          : 'bg-blue-400'
                                        }`}
                                    >
                                      <Calendar className="w-3.5 h-3.5 text-white" />
                                    </div>
                                    <div>
                                      <span
                                        className={`font-semibold text-xs ${isToday || entry.isRunning ? 'text-teal-600 dark:text-teal-400' : 'text-gray-900 dark:text-gray-100'
                                          }`}
                                      >
                                        {formatDate(entry.date)}
                                      </span>
                                      <span className="block text-[10px] text-gray-400 dark:text-gray-500">{dateStr}</span>
                                    </div>
                                  </div>
                                </td>
                                {/* TIME */}
                                <td className="px-4 py-3">
                                  {entry.isRunning ? (
                                    <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-full border border-teal-200">
                                      <span className="text-teal-600 font-semibold text-xs whitespace-nowrap">{formatLocalStartTime(entry)}</span>
                                      <span className="text-teal-300 text-xs">&rarr;</span>
                                      <Timer className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                                      <span className="text-teal-500 font-semibold text-xs whitespace-nowrap">{t('enums.in_progress')}</span>
                                    </div>
                                  ) : (
                                    <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 rounded-full border border-blue-100 dark:border-blue-800">
                                      <span className="text-blue-600 font-semibold text-xs whitespace-nowrap">{formatLocalStartTime(entry)}</span>
                                      <span className="text-blue-300 text-xs">&rarr;</span>
                                      <span className="text-indigo-600 font-semibold text-xs whitespace-nowrap">{formatLocalEndTime(entry)}</span>
                                    </div>
                                  )}
                                </td>
                                {/* HOURS */}
                                <td className="px-4 py-3">
                                  {entry.isRunning ? (
                                    <div className="inline-flex items-center gap-1">
                                      <span className="text-sm font-bold text-teal-600 whitespace-nowrap">
                                        <span className="inline-flex items-center gap-1">
                                          <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-ping"></span>
                                          {(() => {
                                            const elapsed = elapsedMap[entry.id];
                                            if (!elapsed) return '0m';
                                            const parts = elapsed.split(':');
                                            const hrs = parseInt(parts[0], 10);
                                            const mins = parseInt(parts[1], 10);
                                            if (hrs > 0)
                                              return (
                                                <>
                                                  {hrs}
                                                  <span className="text-xs font-medium text-teal-400 ml-0.5">h</span> {mins}
                                                  <span className="text-xs font-medium text-teal-400 ml-0.5">m</span>
                                                </>
                                              );
                                            return (
                                              <>
                                                {mins}
                                                <span className="text-xs font-medium text-teal-400 ml-0.5">m</span>
                                              </>
                                            );
                                          })()}
                                        </span>
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-sm font-bold text-indigo-600 whitespace-nowrap">
                                      {formatHoursDisplay(entry).value}
                                      <span className="text-xs font-medium text-indigo-400 ml-0.5">{formatHoursDisplay(entry).unit}</span>
                                    </span>
                                  )}
                                </td>
                                {/* PRODUCTION TASK */}
                                <td className="px-4 py-3">
                                  {entry.referenceProductionTaskId ? (
                                    <div className="flex flex-col gap-1">
                                      <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg w-full">
                                        <span className="text-slate-700 dark:text-slate-300 text-xs font-medium truncate max-w-[100px]">
                                          {entry.referenceProductionTaskId}
                                        </span>
                                        {entry.version > 1 && (
                                          <span className="inline-flex items-center justify-center px-1 py-0.5 rounded text-[8px] font-bold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                                            v{entry.version}
                                          </span>
                                        )}
                                        {(entry.notes || entry.description) && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setNotesViewEntry(entry);
                                            }}
                                            className="ml-auto p-0.5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors flex-shrink-0"
                                            title={t('pages.timeEntry.notesHistoryTitle')}
                                          >
                                            <Eye className="w-3 h-3 text-indigo-500 dark:text-indigo-400" />
                                          </button>
                                        )}
                                      </div>
                                      {entry.productionTaskId && (
                                        <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 rounded-lg w-full">
                                          <span className="text-purple-600 dark:text-purple-400 text-[10px] font-semibold flex-shrink-0">
                                            {t('pages.reports.taskId')}:
                                          </span>
                                          <span className="text-purple-700 dark:text-purple-300 text-[10px] font-medium truncate">
                                            {entry.productionTaskId}
                                          </span>
                                        </div>
                                      )}
                                      {(entry.activityName || entry.activityId) && (
                                        <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-lg w-full">
                                          <Layers className="w-2.5 h-2.5 text-indigo-500 flex-shrink-0" />
                                          <span className="text-indigo-600 dark:text-indigo-400 text-[10px] font-medium truncate">
                                            {entry.activityName || entry.activityId}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  ) : entry.description ? (
                                    <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg w-full">
                                      <span className="text-slate-700 dark:text-slate-300 text-xs font-medium truncate">{entry.description}</span>
                                      {entry.notes && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setNotesViewEntry(entry);
                                          }}
                                          className="ml-auto p-0.5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors flex-shrink-0"
                                          title={t('pages.timeEntry.notesHistoryTitle')}
                                        >
                                          <Eye className="w-3 h-3 text-indigo-500 dark:text-indigo-400" />
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </td>
                                {/* STATUS */}
                                <td className="px-4 py-3">
                                  {entry.isRunning ? (
                                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-teal-100 to-emerald-100 dark:from-teal-900/40 dark:to-emerald-900/40 rounded-full border border-teal-200 dark:border-teal-800 whitespace-nowrap">
                                      <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse"></span>
                                      <span className="text-[10px] font-bold text-teal-700 dark:text-teal-400">{t('enums.in_progress').toUpperCase()}</span>
                                    </div>
                                  ) : entry.processingStatusCode ? (
                                    <div
                                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full whitespace-nowrap ${entry.processingStatusCode === 'Finished'
                                        ? 'bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800'
                                        : entry.processingStatusCode === 'In Process'
                                          ? 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800'
                                          : entry.processingStatusCode === 'Not Started'
                                            ? 'bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                                            : 'bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800'
                                        }`}
                                    >
                                      <div
                                        className={`w-1.5 h-1.5 rounded-full ${entry.processingStatusCode === 'Finished'
                                          ? 'bg-green-500'
                                          : entry.processingStatusCode === 'In Process'
                                            ? 'bg-yellow-500'
                                            : entry.processingStatusCode === 'Not Started'
                                              ? 'bg-gray-500'
                                              : 'bg-blue-500'
                                          }`}
                                      ></div>
                                      <span
                                        className={`text-[10px] font-bold ${entry.processingStatusCode === 'Finished'
                                          ? 'text-green-700 dark:text-green-400'
                                          : entry.processingStatusCode === 'In Process'
                                            ? 'text-yellow-700 dark:text-yellow-400'
                                            : entry.processingStatusCode === 'Not Started'
                                              ? 'text-gray-700 dark:text-gray-400'
                                              : 'text-blue-700 dark:text-blue-400'
                                          }`}
                                      >
                                        {entry.processingStatusCode === 'Finished' ? t('enums.completed') :
                                          entry.processingStatusCode === 'In Process' ? t('enums.in_progress') :
                                            entry.processingStatusCode === 'Not Started' ? t('commonUi.none') :
                                              entry.processingStatusCode}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-[10px] text-gray-400 dark:text-gray-500">—</span>
                                  )}
                                </td>
                                {/* USER */}
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg flex-shrink-0">
                                      <User className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                                    </div>
                                    <div className="min-w-0">
                                      <span className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate block max-w-[80px]">
                                        {entry.employeeName || employeeName}
                                      </span>
                                      <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate block max-w-[80px]">
                                        {entry.employeeUUId || employeeUUId}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                {/* ACTIONS */}
                                <td className="px-4 py-3">
                                  <div className="flex items-center justify-center gap-1">
                                    {entry.isRunning && (
                                      <button
                                        onClick={() => {
                                          openStopModal(entry.id);
                                          if (entry.activityId)
                                            setStopActivity({
                                              activityId: entry.activityId,
                                              activityDescription: entry.activityName || entry.activityId,
                                            });
                                          else setStopActivity(null);
                                        }}
                                        className="p-1.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg shadow-sm hover:shadow-md hover:scale-105 transition-all flex-shrink-0"
                                        title={t('commonUi.stop')}
                                      >
                                        <Square className="w-3.5 h-3.5 fill-current" />
                                      </button>
                                    )}
                                    {!entry.isRunning &&
                                      entry.referenceProductionTaskId &&
                                      (entry.activityProcessingStatusCode || entry.processingStatusCode) !== 'Finished' &&
                                      !isChild && (
                                        <button
                                          onClick={() =>
                                            setShowCompleteConfirm({
                                              taskId: entry.productionTaskId || entry.referenceProductionTaskId,
                                              activityId: entry.activityId || null,
                                              activityName: entry.activityName || null,
                                            })
                                          }
                                          className="flex items-center gap-1 px-2.5 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg shadow-sm hover:shadow-md hover:scale-105 transition-all flex-shrink-0"
                                          title={t('commonUi.complete')}
                                        >
                                          <CheckCircle className="w-3.5 h-3.5" />
                                          <span className="text-[11px] font-semibold hidden lg:inline">{t('commonUi.complete')}</span>
                                        </button>
                                      )}
                                    {!entry.isRunning &&
                                      entry.referenceProductionTaskId &&
                                      (entry.activityProcessingStatusCode || entry.processingStatusCode) === 'Finished' &&
                                      !isChild && (
                                        <div className="flex items-center justify-center gap-1 px-1.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg text-[10px] font-medium whitespace-nowrap">
                                          <CheckCircle className="w-3 h-3 flex-shrink-0" />
                                          <span className="hidden sm:inline">{t('enums.completed')}</span>
                                        </div>
                                      )}
                                  </div>
                                </td>
                              </tr>
                            );
                          };

                          return groups.flatMap((group, gi) => {
                            if (group.entries.length === 1) {
                              return [renderEntryRow(group.entries[0], gi)];
                            }
                            const isExpanded = expandedTaskGroups[group.key];
                            const latest = group.entries[0];
                            const totalMinutes = group.entries.reduce((sum, e) => {
                              if (e.isRunning) return sum;
                              return sum + (e.hours || 0) * 60;
                            }, 0);
                            const totalHrs = Math.floor(totalMinutes / 60);
                            const totalMins = Math.round(totalMinutes % 60);
                            const hasRunning = group.entries.some((e) => e.isRunning);
                            const activityStatus = latest.activityProcessingStatusCode || latest.processingStatusCode;

                            const parentRow = (
                              <tr
                                key={`group-${group.key}`}
                                className={`cursor-pointer hover:bg-indigo-50/60 dark:hover:bg-indigo-900/20 transition-all duration-200 border-l-4 ${gi % 2 === 0
                                  ? 'border-l-indigo-400 dark:border-l-indigo-500 bg-indigo-50/30 dark:bg-indigo-950/20'
                                  : 'border-l-teal-400 dark:border-l-teal-500 bg-teal-50/30 dark:bg-teal-950/20'
                                  }`}
                                onClick={() =>
                                  setExpandedTaskGroups((prev) => ({ ...prev, [group.key]: !prev[group.key] }))
                                }
                              >
                                <td className="px-4 py-4">
                                  <div className="flex items-center gap-2">
                                    <div
                                      className={`p-2 rounded-xl shadow-sm flex-shrink-0 ${hasRunning ? 'bg-gradient-to-br from-teal-400 to-emerald-500' : 'bg-indigo-500'
                                        }`}
                                    >
                                      {isExpanded ? <ChevronUp className="w-4 h-4 text-white" /> : <ChevronDown className="w-4 h-4 text-white" />}
                                    </div>
                                    <div>
                                      <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">{group.entries.length} {t('commonUi.sessions')}</span>
                                      <span className="block text-[10px] text-gray-400 dark:text-gray-500">
                                        {t('commonUi.clickTo')} {isExpanded ? t('commonUi.collapse') : t('commonUi.expand')}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-4">
                                  {hasRunning ? (
                                    <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-full border border-teal-200">
                                      <Timer className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                                      <span className="text-teal-500 font-semibold text-xs whitespace-nowrap">{t('enums.in_progress')}</span>
                                    </div>
                                  ) : (
                                    <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 rounded-full border border-blue-100 dark:border-blue-800">
                                      <span className="text-blue-600 font-semibold text-xs whitespace-nowrap">
                                        v1 — v{Math.max(...group.entries.map((e) => e.version || 1))}
                                      </span>
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-4">
                                  <span className="text-base font-bold text-indigo-600 whitespace-nowrap">
                                    {totalHrs > 0 && (
                                      <>
                                        {totalHrs}
                                        <span className="text-xs font-medium text-indigo-400 ml-0.5">{t('commonUi.hrs')}</span>{' '}
                                      </>
                                    )}
                                    {totalMins}
                                    <span className="text-xs font-medium text-indigo-400 ml-0.5">m</span>
                                  </span>
                                  <span className="block text-[10px] text-gray-400">{t('commonUi.total')}</span>
                                </td>
                                <td className="px-4 py-4">
                                  <div className="flex flex-col gap-1">
                                    <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg w-full">
                                      <span className="text-slate-700 dark:text-slate-300 text-xs font-bold truncate max-w-[100px]">
                                        {latest.referenceProductionTaskId}
                                      </span>
                                      <span className="inline-flex items-center justify-center px-1 py-0.5 rounded text-[8px] font-bold bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 flex-shrink-0">
                                        {group.entries.length}x
                                      </span>
                                    </div>
                                    {/* ADDED: Show activity name in parent row for better context */}
                                    {(group.activityName || latest.activityName) && (
                                      <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-lg w-full">
                                        <Layers className="w-2.5 h-2.5 text-indigo-500 flex-shrink-0" />
                                        <span className="text-indigo-600 dark:text-indigo-400 text-[10px] font-medium truncate">
                                          Activity: {group.activityName || latest.activityName}
                                        </span>
                                      </div>
                                    )}
                                    {latest.productionTaskId && (
                                      <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 rounded-lg w-full">
                                        <span className="text-purple-600 dark:text-purple-400 text-[10px] font-semibold flex-shrink-0">
                                          {t('pages.reports.taskId')}:
                                        </span>
                                        <span className="text-purple-700 dark:text-purple-300 text-[10px] font-medium truncate">
                                          {latest.productionTaskId}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-4">
                                  {hasRunning ? (
                                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-teal-100 to-emerald-100 dark:from-teal-900/40 dark:to-emerald-900/40 rounded-full border border-teal-200 dark:border-teal-800 whitespace-nowrap">
                                      <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse"></span>
                                      <span className="text-[10px] font-bold text-teal-700 dark:text-teal-400">{t('enums.in_progress').toUpperCase()}</span>
                                    </div>
                                  ) : latest.processingStatusCode ? (
                                    <div
                                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full whitespace-nowrap ${latest.processingStatusCode === 'Finished'
                                        ? 'bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800'
                                        : latest.processingStatusCode === 'In Process'
                                          ? 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800'
                                          : 'bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                                        }`}
                                    >
                                      <div
                                        className={`w-1.5 h-1.5 rounded-full ${latest.processingStatusCode === 'Finished'
                                          ? 'bg-green-500'
                                          : latest.processingStatusCode === 'In Process'
                                            ? 'bg-yellow-500'
                                            : 'bg-gray-500'
                                          }`}
                                      ></div>
                                      <span
                                        className={`text-[10px] font-bold ${latest.processingStatusCode === 'Finished'
                                          ? 'text-green-700 dark:text-green-400'
                                          : latest.processingStatusCode === 'In Process'
                                            ? 'text-yellow-700 dark:text-yellow-400'
                                            : 'text-gray-700 dark:text-gray-400'
                                          }`}
                                      >
                                        {latest.processingStatusCode === 'Finished' ? t('enums.completed') :
                                          latest.processingStatusCode === 'In Process' ? t('enums.in_progress') :
                                            latest.processingStatusCode === 'Not Started' ? t('commonUi.none') :
                                              latest.processingStatusCode}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-[10px] text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-4">
                                  <div className="flex items-center gap-2">
                                    <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg flex-shrink-0">
                                      <User className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                                    </div>
                                    <div className="min-w-0">
                                      <span className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate block max-w-[80px]">
                                        {latest.employeeName || employeeName}
                                      </span>
                                      <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate block max-w-[80px]">
                                        {latest.employeeUUId || employeeUUId}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                                  <div className="flex items-center justify-center gap-1">
                                    {hasRunning &&
                                      (() => {
                                        const runningEntry = group.entries.find((e) => e.isRunning);
                                        return runningEntry ? (
                                          <button
                                            onClick={() => {
                                              openStopModal(runningEntry.id);
                                              if (runningEntry.activityId)
                                                setStopActivity({
                                                  activityId: runningEntry.activityId,
                                                  activityDescription: runningEntry.activityName || runningEntry.activityId,
                                                });
                                              else setStopActivity(null);
                                            }}
                                            className="p-1.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg shadow-sm hover:shadow-md hover:scale-105 transition-all flex-shrink-0"
                                            title={t('commonUi.stop')}
                                          >
                                            <Square className="w-3.5 h-3.5 fill-current" />
                                          </button>
                                        ) : null;
                                      })()}
                                    {!hasRunning && activityStatus !== 'Finished' && (
                                      <button
                                        onClick={() => setShowCompleteConfirm({
                                          taskId: latest.productionTaskId || latest.referenceProductionTaskId,
                                          activityId: latest.activityId || group.activityId || null,
                                          activityName: latest.activityName || group.activityName || null,
                                        })}
                                        className="flex items-center gap-1 px-2.5 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg shadow-sm hover:shadow-md hover:scale-105 transition-all flex-shrink-0"
                                        title={t('commonUi.complete')}
                                      >
                                        <CheckCircle className="w-3.5 h-3.5" />
                                        <span className="text-[11px] font-semibold hidden lg:inline">{t('commonUi.complete')}</span>
                                      </button>
                                    )}
                                    {!hasRunning && activityStatus === 'Finished' && (
                                      <div className="flex items-center justify-center gap-1 px-1.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg text-[10px] font-medium whitespace-nowrap">
                                        <CheckCircle className="w-3 h-3 flex-shrink-0" />
                                        <span className="hidden sm:inline">{t('enums.completed')}</span>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                            const childRows = isExpanded ? group.entries.map((entry, ci) => renderEntryRow(entry, ci, { isChild: true })) : [];
                            return [parentRow, ...childRows];
                          });
                        })()}
                        {entriesPageLoading &&
                          viewMode === 'list' &&
                          timeEntries.length > 0 &&
                          [1, 2, 3].map((sk) => (
                            <tr key={`load-more-sk-${sk}`}>
                              <td colSpan={7} className="px-4 py-3">
                                <Skeleton className="h-11 w-full rounded-lg" />
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {entriesTotalCount > 0 && viewMode === 'timeline' && entriesPage < entriesTotalPages && (
                <div ref={listScrollSentinelRef} className="h-2 w-full shrink-0" aria-hidden />
              )}

              {/* Pagination Controls */}
              {entriesTotalCount > 0 && (
                <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-3">
                    <span>
                      {t('commonUi.showing')} {timeEntries.length} {t('commonUi.of')} {entriesTotalCount} {t('commonUi.entries')}
                      {entriesTotalPages > 1 && ` — ${t('commonUi.page')} ${entriesPage} ${t('commonUi.of')} ${entriesTotalPages}`}

                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-400">{t('commonUi.show')}:</span>
                      <select
                        value={entriesPageSize}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        disabled={entriesPageLoading}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 cursor-pointer shadow-sm hover:border-gray-400 dark:hover:border-gray-500 appearance-none bg-no-repeat bg-right pr-8"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                          backgroundPosition: 'right 8px center',
                        }}
                      >
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {entriesTotalPages > 1 && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEntriesPageChange(entriesPage - 1)}
                        disabled={entriesPage <= 1 || entriesPageLoading}
                        className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      {(() => {
                        const pages = [];
                        let start = Math.max(1, entriesPage - 2);
                        let end = Math.min(entriesTotalPages, start + 4);
                        if (end - start < 4) start = Math.max(1, end - 4);
                        for (let i = start; i <= end; i++) {
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handleEntriesPageChange(i)}
                              disabled={entriesPageLoading}
                              className={`min-w-[28px] h-7 rounded-lg text-xs font-semibold transition-all ${i === entriesPage
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                                } disabled:opacity-50`}
                            >
                              {i}
                            </button>
                          );
                        }
                        return pages;
                      })()}
                      <button
                        onClick={() => handleEntriesPageChange(entriesPage + 1)}
                        disabled={entriesPage >= entriesTotalPages || entriesPageLoading}
                        className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* All Modals - keep as is from your original */}
          {/* Start Timer Confirmation Modal */}
          {showStartConfirm && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md animate-slide-up p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-teal-100 dark:bg-teal-900/40 rounded-xl">
                    <Play className="w-6 h-6 text-teal-600 dark:text-teal-400 fill-current" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {continueFromEntry ? t('commonUi.continueTimer') : t('pages.timeEntry.modalStartTitle')}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {continueFromEntry
                        ? `${t('commonUi.continuingFrom')} v${continueFromEntry.version} — ${t('commonUi.timerWillStartAt')} ${(() => {
                          const totalSecs =
                            Math.round((continueFromEntry.hours || 0) * 3600) + (continueFromEntry.accumulatedSeconds || 0);
                          const h = Math.floor(totalSecs / 3600);
                          const m = Math.floor((totalSecs % 3600) / 60);
                          return h > 0 ? `${h}h ${m}m` : `${m}m`;
                        })()}`
                        : t('pages.timeEntry.modalStartTitle')}
                    </p>
                  </div>
                </div>
                {barcodeTask && (
                  <div
                    className={`mt-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-4 border ${barcodeTask.processingStatusCode === '3'
                      ? 'border-red-300 dark:border-red-700 bg-red-50/50 dark:bg-red-900/20'
                      : 'border-blue-200 dark:border-blue-700'
                      }`}
                  >
                    <p className="text-xs text-blue-500 dark:text-blue-400 font-medium mb-2">{t('pages.dashboard.statProductionTasks')}</p>
                    <p className="text-base font-bold text-gray-900 dark:text-gray-100">
                      {barcodeTask.productionTaskId || barcodeTask.id || t('commonUi.unknown')}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-600 dark:text-gray-400">
                      {barcodeTask.productionTaskId && (
                        <span>
                          {t('pages.reports.taskId')}: <strong className="text-gray-800 dark:text-gray-200">{barcodeTask.productionTaskId}</strong>
                        </span>
                      )}
                      {barcodeTask.processingStatusCode && (
                        <span className="inline-flex items-center gap-1">
                          {t('commonUi.status')}:{' '}
                          <strong
                            className={`px-1.5 py-0.5 rounded text-xs ${barcodeTask.processingStatusCode === '3'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 font-bold'
                              : barcodeTask.processingStatusCode === 'COMPLETED'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                                : barcodeTask.processingStatusCode === 'IN_PROGRESS'
                                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400'
                                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                              }`}
                          >
                            {barcodeTask.processingStatusCode === '3' ? t('enums.completed') : barcodeTask.processingStatusCode}
                          </strong>
                        </span>
                      )}
                      {barcodeTask.operationTypeCode && (
                        <span>
                          {t('commonUi.operation')}: <strong className="text-gray-800 dark:text-gray-200">{barcodeTask.operationTypeCode}</strong>
                        </span>
                      )}
                      {barcodeTask.productionLotId && (
                        <span>
                          {t('commonUi.lot')}: <strong className="text-gray-800 dark:text-gray-200">{barcodeTask.productionLotId}</strong>
                        </span>
                      )}
                    </div>
                    {barcodeTask.processingStatusCode === '3' && (
                      <div className="mt-3 p-2 bg-red-100 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-800">
                        <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {t('pages.timeEntry.noTasksBody')}
                        </p>
                      </div>
                    )}
                    {barcodeTask.barcodeBase64 && (
                      <div className="mt-3 p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 inline-block">
                        <img src={`data:image/png;base64,${barcodeTask.barcodeBase64}`} alt={t('commonUi.barcode')} className="h-16 object-contain" />
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowStartConfirm(false);
                      setContinueFromEntry(null);
                    }}
                    className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t('commonUi.cancel')}
                  </button>
                  <button
                    onClick={() => {
                      setShowStartConfirm(false);
                      const accSecs = continueFromEntry
                        ? Math.round((continueFromEntry.hours || 0) * 3600) + (continueFromEntry.accumulatedSeconds || 0)
                        : 0;
                      const parentId = continueFromEntry?.id || null;
                      handleStartTimer(barcodeTask?.id || '', accSecs, parentId);
                      setContinueFromEntry(null);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white rounded-xl font-medium transition-colors"
                  >
                    <Play className="w-4 h-4 fill-current" /> {continueFromEntry ? t('commonUi.continue') : t('commonUi.start')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Previous Sessions Popup */}
          {showPreviousSessions && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg animate-slide-up max-h-[80vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-amber-100 dark:bg-amber-900/40 rounded-xl">
                      <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('pages.timeEntry.notesHistoryTitle')}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('pages.reports.task')} <strong>{barcodeTask?.productionTaskId}</strong> {t('commonUi.has')} {taskHistory.length} {t('commonUi.pastSession')}{taskHistory.length > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {taskHistory.map((entry) => {
                    const hrs = entry.hours || 0;
                    const accSecs = entry.accumulatedSeconds || 0;
                    const totalMins = Math.round(hrs * 60 + accSecs / 60);
                    const displayTime = totalMins >= 60 ? `${Math.floor(totalMins / 60)}h ${totalMins % 60}m` : `${totalMins}m`;

                    // Format the start time properly
                    const formatStartTime = (startTime) => {
                      if (!startTime) return '';
                      // Convert 24-hour format to 12-hour format with AM/PM
                      const [hours, minutes] = startTime.split(':');
                      const hour = parseInt(hours, 10);
                      const ampm = hour >= 12 ? 'PM' : 'AM';
                      const hour12 = hour % 12 || 12;
                      return `${hour12}:${minutes} ${ampm}`;
                    };

                    // Format the end time properly
                    const formatEndTime = (endTime) => {
                      if (!endTime) return '';
                      if (endTime === '-') return '-';
                      const [hours, minutes] = endTime.split(':');
                      const hour = parseInt(hours, 10);
                      const ampm = hour >= 12 ? 'PM' : 'AM';
                      const hour12 = hour % 12 || 12;
                      return `${hour12}:${minutes} ${ampm}`;
                    };

                    // Get the date string (just the date part)
                    const getDateString = (dateStr) => {
                      if (!dateStr) return '';
                      const parts = dateStr.slice(0, 10).split('-');
                      const date = new Date(+parts[0], +parts[1] - 1, +parts[2]);
                      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
                    };

                    return (
                      <div
                        key={entry.id}
                        className={`p-3 rounded-xl border transition-all cursor-pointer hover:shadow-md ${continueFromEntry?.id === entry.id
                          ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/30 dark:border-teal-600 ring-2 ring-teal-300'
                          : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 hover:border-blue-300'
                          }`}
                        onClick={() => setContinueFromEntry(continueFromEntry?.id === entry.id ? null : entry)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 text-xs font-bold">
                              v{entry.version}
                            </span>
                            <div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{entry.employeeName}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {getDateString(entry.date)}
                                {entry.startTime && (
                                  <>
                                    {' '}
                                    <span className="font-mono">
                                      {formatLocalStartTime(entry)} → {entry.endTime ? formatLocalEndTime(entry) : t('enums.in_progress')}
                                    </span>
                                  </>
                                )}
                                {' • '}
                                <span className="font-medium">{displayTime}</span>
                                {entry.activityName && <span> • {entry.activityName}</span>}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{displayTime}</span>
                            {!entry.isLatestVersion && <p className="text-xs text-gray-400">{t('commonUi.superseded')}</p>}
                          </div>
                        </div>
                        {entry.description && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">{entry.description}</p>}
                      </div>
                    );
                  })}
                </div>
                <div className="p-4 border-t border-gray-100 dark:border-gray-700 space-y-2">
                  {continueFromEntry && (
                    <div className="p-2 bg-teal-50 dark:bg-teal-900/20 rounded-lg text-xs text-teal-700 dark:text-teal-400 text-center">
                      {t('commonUi.continueFrom')} <strong>v{continueFromEntry.version}</strong> — {t('commonUi.timerWillStartAt')}{' '}
                      <strong>
                        {(() => {
                          const totalSecs =
                            Math.round((continueFromEntry.hours || 0) * 3600) + (continueFromEntry.accumulatedSeconds || 0);
                          const h = Math.floor(totalSecs / 3600);
                          const m = Math.floor((totalSecs % 3600) / 60);
                          return h > 0 ? `${h}h ${m}m` : `${m}m`;
                        })()}
                      </strong>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowPreviousSessions(false);
                        setContinueFromEntry(null);
                      }}
                      className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      {t('commonUi.cancel')}
                    </button>
                    <button
                      onClick={() => {
                        setShowPreviousSessions(false);
                        setContinueFromEntry(null);
                        setShowStartConfirm(true);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-xl font-medium transition-colors"
                    >
                      <Plus className="w-4 h-4" /> {t('commonUi.freshStart')}
                    </button>
                    {continueFromEntry && (
                      <button
                        onClick={() => {
                          setShowPreviousSessions(false);
                          setShowStartConfirm(true);
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white rounded-xl font-medium transition-colors"
                      >
                        <Play className="w-4 h-4 fill-current" /> {t('commonUi.continue')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stop Timer Modal */}
          {showStopConfirm && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md animate-slide-up p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-red-100 dark:bg-red-900/40 rounded-xl">
                    <Square className="w-6 h-6 text-red-600 dark:text-red-400 fill-current" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('commonUi.stopTimer')}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('commonUi.stopTimerDesc')}</p>
                  </div>
                </div>
                {stopModalActivities.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      <Layers className="w-3.5 h-3.5 inline-block mr-1" />{t('commonUi.activity')}
                    </label>
                    <select
                      value={stopActivity?.activityId || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) {
                          setStopActivity(null);
                        } else {
                          const act = stopModalActivities.find((a) => a.activityId === val);
                          setStopActivity(act || null);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-400 focus:border-transparent text-sm"
                    >
                      <option value="">{t('commonUi.allActivities')}</option>
                      {stopModalActivities.map((act) => (
                        <option key={act.activityId} value={act.activityId}>
                          {act.activityDescription || act.activityId}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('commonUi.notes')}</label>
                  <textarea
                    value={stopNotes}
                    onChange={(e) => setStopNotes(e.target.value)}
                    placeholder={t('pages.timeEntry.notesPlaceholder')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-red-400 focus:border-transparent resize-none text-sm"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowStopConfirm(null);
                      setStopNotes('');
                      setStopActivity(null);
                    }}
                    className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t('commonUi.cancel')}
                  </button>
                  <button
                    onClick={() => {
                      const id = showStopConfirm;
                      const actId = stopActivity?.activityId || null;
                      const actName = stopActivity?.activityDescription || stopActivity?.activityId || null;
                      setShowStopConfirm(null);
                      handleStopTimer(id, stopNotes, actId, actName);
                      setStopNotes('');
                      setStopActivity(null);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white rounded-xl font-medium transition-colors"
                  >
                    <Square className="w-4 h-4 fill-current" /> {t('commonUi.stop')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Mark as Completed Modal */}
          {showCompleteConfirm && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md animate-slide-up p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-green-100 dark:bg-green-900/40 rounded-xl">
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('commonUi.markCompleted')}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('commonUi.markCompletedDesc')}
                    </p>
                  </div>
                </div>
                <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/30 rounded-xl border border-amber-200 dark:border-amber-700">
                  <p className="text-xs text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5" /> {t('commonUi.actionUndone')}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                    {t('commonUi.markCompletedWarning')}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCompleteConfirm(null)}
                    disabled={completing}
                    className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    {t('commonUi.cancel')}
                  </button>
                  <button
                    onClick={() => handleCompleteTask(showCompleteConfirm)}
                    disabled={completing}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                  >
                    {completing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    {completing ? t('commonUi.completing') : t('commonUi.complete')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* New Time Entry Modal */}
          {showModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xl animate-slide-up max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white dark:bg-gray-800 flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700 z-10">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-900/40">
                      <Plus className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('pages.timeEntry.modalStartTitle')}</h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.timeEntry.barcodePlaceholder')}</p>
                    </div>
                  </div>
                  <button onClick={closeModal} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="p-6 space-y-5">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <ScanBarcode className="w-4 h-4 text-sap-blue" />
                      <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('commonUi.scanBarcode')}</label>
                    </div>
                    <div className="flex gap-2">
                      <input
                        ref={barcodeRef}
                        type="text"
                        placeholder={t('pages.timeEntry.barcodePlaceholder')}
                        value={barcode}
                        onChange={(e) => {
                          setBarcode(e.target.value);
                          setBarcodeError('');
                          setBarcodeTask(null);
                          setSelectedActivity(null);
                          setTaskHistory([]);
                          setContinueFromEntry(null);
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && handleBarcodeSearch()}
                        className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-sap-blue focus:border-transparent text-gray-900 dark:text-gray-100 dark:bg-gray-700 placeholder-gray-400 dark:placeholder-gray-500"
                      />
                      <button
                        type="button"
                        onClick={handleBarcodeSearch}
                        disabled={barcodeLoading || !barcode.trim()}
                        className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-sap-blue to-primary-600 text-white rounded-xl font-semibold transition-all shadow-sm hover:shadow-md disabled:opacity-50"
                      >
                        {barcodeLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        {t('commonUi.search')}
                      </button>
                    </div>
                    {barcodeError && (
                      <p className="mt-2 flex items-center gap-1.5 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" /> {barcodeError}
                      </p>
                    )}
                    {barcodeTask && (
                      <div className="mt-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
                        <p className="text-xs text-blue-500 dark:text-blue-400 font-medium mb-2">{t('pages.dashboard.statProductionTasks')}</p>
                        <p className="text-base font-bold text-gray-900 dark:text-gray-100">
                          {barcodeTask.productionTaskId || barcodeTask.id || t('commonUi.unknown')}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-600 dark:text-gray-400">
                          {barcodeTask.productionTaskId && (
                            <span>
                              {t('pages.reports.taskId')}: <strong className="text-gray-800 dark:text-gray-200">{barcodeTask.productionTaskId}</strong>
                            </span>
                          )}
                          {barcodeTask.processingStatusCode && (
                            <span className="inline-flex items-center gap-1">
                              {t('commonUi.status')}:{' '}
                              <strong
                                className={`px-1.5 py-0.5 rounded text-xs ${barcodeTask.processingStatusCode === 'COMPLETED'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                                  : barcodeTask.processingStatusCode === 'IN_PROGRESS'
                                    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400'
                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                  }`}
                              >
                                {barcodeTask.processingStatusCode === 'COMPLETED' ? t('enums.completed') :
                                  barcodeTask.processingStatusCode === 'IN_PROGRESS' ? t('enums.in_progress') :
                                    barcodeTask.processingStatusCode}
                              </strong>
                            </span>
                          )}
                          {barcodeTask.operationTypeCode && (
                            <span>
                              {t('commonUi.operation')}: <strong className="text-gray-800 dark:text-gray-200">{barcodeTask.operationTypeCode}</strong>
                            </span>
                          )}
                          {barcodeTask.productionLotId && (
                            <span>
                              {t('commonUi.lot')}: <strong className="text-gray-800 dark:text-gray-200">{barcodeTask.productionLotId}</strong>
                            </span>
                          )}
                        </div>
                        {barcodeTask.barcodeBase64 && (
                          <div className="mt-3 p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 inline-block">
                            <img src={`data:image/png;base64,${barcodeTask.barcodeBase64}`} alt={t('commonUi.barcode')} className="h-16 object-contain" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {barcodeTask?.activities?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Layers className="w-4 h-4 text-indigo-500" />
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('commonUi.selectActivity')}</label>
                      </div>
                      <select
                        value={selectedActivity?.activityId || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val) {
                            setSelectedActivity(null);
                            return;
                          }
                          const act = barcodeTask.activities.find((a) => a.activityId === val);
                          if (act?.processingStatusCode === '3') return;
                          if (act?.runningByEmployeeName) return;
                          setSelectedActivity(act || null);
                        }}
                        disabled={barcodeTask?.processingStatusCode === '3'}
                        className={`w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 dark:text-gray-100 dark:bg-gray-700 bg-white appearance-none cursor-pointer ${barcodeTask?.processingStatusCode === '3' ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                      >
                        <option value="">{t('commonUi.allActivities')} ({barcodeTask.activities.length})</option>
                        {barcodeTask.activities.map((act) => (
                          <option
                            key={act.activityId}
                            value={act.activityId}
                            disabled={act.processingStatusCode === '3' || !!act.runningByEmployeeName}
                          >
                            {act.processingStatusCode === '3' ? '✅ ' : act.runningByEmployeeName ? '🔒 ' : ''}
                            {act.description || act.activityId}
                            {act.plannedDuration ? ` — ${act.plannedDuration}` : ''}
                            {act.processingStatusCode === '3'
                              ? ` (${t('enums.completed')})`
                              : act.runningByEmployeeName
                                ? ` (${t('commonUi.inUseBy')} ${act.runningByEmployeeName})`
                                : ''}
                          </option>
                        ))}
                      </select>
                      {selectedActivity && (
                        <div className="mt-2 text-xs text-indigo-600 dark:text-indigo-400 flex flex-wrap gap-3">
                          <span>
                            {t('commonUi.activity')}: <strong>{selectedActivity.description || selectedActivity.activityId}</strong>
                          </span>
                          {selectedActivity.plannedDuration && (
                            <span>
                              {t('commonUi.planned')}: <strong>{selectedActivity.plannedDuration}</strong>
                            </span>
                          )}
                          {selectedActivity.operationId && (
                            <span>
                              {t('commonUi.operation')}: <strong>{selectedActivity.operationId}</strong>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      {t('commonUi.description')} <span className="text-gray-400 dark:text-gray-500 text-xs font-normal">{t('commonUi.optional')}</span>
                    </label>
                    <input
                      type="text"
                      value={timerDescription}
                      onChange={(e) => setTimerDescription(e.target.value)}
                      placeholder={t('pages.timeEntry.workDescPlaceholder')}
                      disabled={barcodeTask?.processingStatusCode === '3'}
                      className={`w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-sap-blue focus:border-transparent text-gray-900 dark:text-gray-100 dark:bg-gray-700 placeholder-gray-400 dark:placeholder-gray-500 ${barcodeTask?.processingStatusCode === '3' ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    />
                  </div>

                  {barcodeTask && taskHistory.length > 0 && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                          {taskHistory.length} {t('commonUi.previousSession')}{taskHistory.length > 1 ? 's' : ''} {t('commonUi.found')}
                        </span>
                      </div>
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        {t('commonUi.totalLogged')}:{' '}
                        <strong>
                          {(() => {
                            const totalSecs = taskHistory.reduce(
                              (sum, e) => sum + (e.hours || 0) * 3600 + (e.accumulatedSeconds || 0),
                              0
                            );
                            const h = Math.floor(totalSecs / 3600);
                            const m = Math.floor((totalSecs % 3600) / 60);
                            return h > 0 ? `${h}h ${m}m` : `${m}m`;
                          })()}
                        </strong>{' '}
                        {t('commonUi.fromSessions')}
                      </p>
                    </div>
                  )}

                  {(barcodeTask && barcodeTask.processingStatusCode === '3') || selectedActivity?.processingStatusCode === '3' ? (
                    <div className="w-full p-5 bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-xl">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 bg-red-100 dark:bg-red-800/30 rounded-full">
                          <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-red-800 dark:text-red-300 font-bold text-base">
                            {selectedActivity?.processingStatusCode === '3' ? t('commonUi.activityCompleted') : t('commonUi.taskCompleted')}
                          </p>
                          <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                            {selectedActivity?.processingStatusCode === '3'
                              ? t('commonUi.activityCompletedDesc')
                              : t('commonUi.taskCompletedDesc')}
                          </p>
                        </div>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-red-200 dark:border-red-800">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          <span className="font-medium text-red-600 dark:text-red-400">{t('commonUi.whyCannotStart')}</span> {t('commonUi.cannotStartDesc')}
                        </p>
                      </div>
                      <div className="mt-3 text-xs text-gray-500 dark:text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {t('pages.reports.taskId')}: {barcodeTask.productionTaskId || barcodeTask.id} • {t('commonUi.completedOn')}:{' '}
                        {barcodeTask.completedAt ? new Date(barcodeTask.completedAt).toLocaleDateString() : t('commonUi.unknownDate')}
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        if (taskHistory.length > 0) {
                          setShowPreviousSessions(true);
                        } else {
                          setShowStartConfirm(true);
                        }
                      }}
                      disabled={!barcodeTask}
                      className="w-full flex items-center justify-center gap-3 px-5 py-3.5 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white rounded-xl font-bold transition-all shadow-md hover:shadow-lg text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Play className="w-6 h-6 fill-current" />
                      {barcodeTask?.processingStatusCode === '2' || barcodeTask?.processingStatusCode === 'IN_PROGRESS'
                        ? t('commonUi.continueWorking')
                        : t('commonUi.startTimer')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Task Picker Modal */}
          <TaskPickerModal
            isOpen={showTaskPicker}
            onClose={() => {
              setShowTaskPicker(false);
              setTaskPickerInitialTaskId(null);
            }}
            tasks={productionTasks}
            runningEntries={runningEntries}
            onStartTimer={handleTaskPickerStartTimer}
            currentPage={taskPickerPage}
            totalPages={taskPickerTotalPages}
            totalCount={taskPickerTotalCount}
            onPageChange={handleTaskPickerPageChange}
            paginationLoading={taskPickerPageLoading}
            initialTaskId={taskPickerInitialTaskId}
            initialOpenKey={taskPickerInitialOpenKey}
            searchTerm={taskPickerSearchTerm}
            filterStatus={taskPickerFilterStatus}
            onSearchTermChange={setTaskPickerSearchTerm}
            onFilterStatusChange={(status) => {
              setTaskPickerFilterStatus(status);
            }}
          />

          {/* Activity Conflict Modal */}
          {activityConflict && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full animate-slide-up p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-amber-100 dark:bg-amber-900/40 rounded-xl">
                    <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('pages.timeEntry.timerConflictTitle')}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.timeEntry.timerConflictSub')}</p>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 border border-teal-200 dark:border-teal-700 rounded-xl p-4 mb-4">
                  <p className="text-xs font-semibold text-teal-600 dark:text-teal-400 uppercase tracking-wider mb-2">{t('pages.timeEntry.currentlyRunning')}</p>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-teal-200/50 dark:bg-teal-800/50 rounded-lg animate-pulse">
                      <Timer className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                        {activityConflict.conflictingEntry.description ||
                          activityConflict.conflictingEntry.referenceProductionTaskId ||
                          t('enums.time_entry')}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {activityConflict.conflictingEntry.activityName && (
                          <span className="text-xs px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full font-medium">
                            {activityConflict.conflictingEntry.activityName}
                          </span>
                        )}
                        {activityConflict.conflictingEntry.startedAt && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {t('commonUi.startedAt')} {formatLocalDateTime(activityConflict.conflictingEntry.startedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4 mb-5">
                  <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-2">{t('pages.timeEntry.wantToStart')}</p>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-200/50 dark:bg-blue-800/50 rounded-lg">
                      <Play className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                        {activityConflict.pendingPayload.description ||
                          activityConflict.pendingPayload.referenceProductionTaskId ||
                          t('pages.timeEntry.newEntryLabel')}
                      </p>
                      {activityConflict.pendingPayload.activityName && (
                        <span className="text-xs px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full font-medium mt-1 inline-block">
                          {activityConflict.pendingPayload.activityName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setActivityConflict(null)}
                    className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                  >
                    {t('commonUi.cancel')}
                  </button>
                  <button
                    onClick={handleStopAndStart}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl font-semibold transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    <Square className="w-4 h-4" />
                    {t('pages.timeEntry.stopAndStart')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notes History Modal */}
          {notesViewEntry && (
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setNotesViewEntry(null)}
            >
              <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-lg">
                      <StickyNote className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('pages.timeEntry.notesHistoryTitle')}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {notesViewEntry.referenceProductionTaskId || t('enums.time_entry')} — {notesViewEntry.employeeName}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setNotesViewEntry(null)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                <div className="px-6 py-5 overflow-y-auto max-h-[60vh] space-y-3">
                  {(() => {
                    const noteColors = ['#FEF3C7', '#DBEAFE', '#D1FAE5', '#FCE7F3', '#EDE9FE', '#FEE2E2'];
                    const darkNoteColors = [
                      'rgba(245,158,11,0.12)',
                      'rgba(59,130,246,0.12)',
                      'rgba(16,185,129,0.12)',
                      'rgba(236,72,153,0.12)',
                      'rgba(139,92,246,0.12)',
                      'rgba(239,68,68,0.12)',
                    ];
                    const allNotes = [];
                    if (notesViewEntry.notes) {
                      notesViewEntry.notes.split('\n---\n').forEach((chunk, i) => {
                        const match = chunk.match(/^\[(.+?)\]\s*([\s\S]*)$/);
                        if (match) {
                          allNotes.push({ timestamp: match[1], text: match[2].trim(), idx: i });
                        } else {
                          allNotes.push({ timestamp: null, text: chunk.trim(), idx: i });
                        }
                      });
                    }
                    if (allNotes.length === 0 && notesViewEntry.description) {
                      notesViewEntry.description.split('\n---\n').forEach((chunk, i) => {
                        allNotes.push({ timestamp: null, text: chunk.trim(), idx: i });
                      });
                    }
                    if (allNotes.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <StickyNote className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                          <p className="text-gray-400 dark:text-gray-500 text-sm">{t('commonUi.noNotes')}</p>
                        </div>
                      );
                    }
                    return allNotes.map((note, i) => (
                      <div
                        key={i}
                        className="w-full rounded-xl p-4 shadow-sm border border-black/5 dark:border-white/5"
                        style={{
                          backgroundColor: document.documentElement.classList.contains('dark')
                            ? darkNoteColors[i % darkNoteColors.length]
                            : noteColors[i % noteColors.length],
                        }}
                      >
                        <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words leading-relaxed">{note.text}</p>
                        {note.timestamp && (
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2 font-mono">{note.timestamp}</p>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          )}
        </>
      )
      }
    </div >
  );
};

export default TimeEntryPage;
